import React, { useState, useEffect, useRef } from 'react';
import mermaid from 'mermaid';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck, Mail, Key, Award, CheckCircle, HelpCircle, FileText, Upload } from 'lucide-react';
import { FaUserCheck, FaExclamationTriangle } from 'react-icons/fa';
import CurrencySymbol from './CurrencySymbol';
import { BUILDER_DEPENDENCIES } from './admin/RestaurantPlan';

mermaid.initialize({ startOnLoad: true, theme: 'dark', flowchart: { useMaxWidth: false, htmlLabels: true } });

const MermaidChart = ({ chart }) => {
  const [svgHtml, setSvgHtml] = useState("");
  const [scale, setScale] = useState(1);

  useEffect(() => {
    let isMounted = true;
    const renderChart = async () => {
      try {
        const id = 'mermaid-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await mermaid.render(id, chart);
        if (isMounted) setSvgHtml(svg);
      } catch (e) {
        console.error("Mermaid parsing error", e);
        if (isMounted) setSvgHtml(`<div style="color:red; background:#fff; padding:20px; border-radius:10px;">Mermaid Error: ${e.message}</div>`);
      }
    };
    renderChart();
    return () => { isMounted = false; };
  }, [chart]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', background: '#0f172a', borderRadius: '16px', border: '1px solid #334155', overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: '15px', padding: '15px', background: '#1e293b', justifyContent: 'center', alignItems: 'center', borderBottom: '1px solid #334155', zIndex: 10 }}>
        <span style={{ color: '#94a3b8', fontWeight: '600', marginRight: '10px' }}>Zoom Controls:</span>
        <button onClick={() => setScale(s => Math.max(0.4, s - 0.2))} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#2563eb'} onMouseOut={e => e.currentTarget.style.background = '#3b82f6'}>-</button>
        <span style={{ color: '#f8fafc', fontWeight: 'bold', width: '60px', textAlign: 'center', fontSize: '1.1rem' }}>{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(2, s + 0.2))} style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#2563eb'} onMouseOut={e => e.currentTarget.style.background = '#3b82f6'}>+</button>
        <button onClick={() => setScale(1)} style={{ background: '#64748b', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', marginLeft: '10px', height: '40px', transition: 'background 0.2s' }} onMouseOver={e => e.currentTarget.style.background = '#475569'} onMouseOut={e => e.currentTarget.style.background = '#64748b'}>Reset</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div 
          className="mermaid-container"
          style={{ width: 'max-content', display: 'flex', justifyContent: 'center', margin: '0 auto', zoom: scale, transition: 'zoom 0.2s ease-in-out' }} 
          dangerouslySetInnerHTML={{ __html: svgHtml }} 
        />
      </div>
    </div>
  );
};

const generateSystemFlowchart = (view, purchasedAddons, subscriptionPlansData) => {
  let activeModules = new Set();
  let activePages = new Set();
  
  if (view === 'All') {
    ['mod_pos_dashboard', 'mod_pos_billing', 'mod_table_mgmt', 'mod_kitchen_mgmt', 'mod_customer_mgmt', 'mod_inventory_mgmt', 'mod_purchase_mgmt', 'mod_hr_payroll', 'mod_reports', 'mod_multi_branch', 'mod_role_mgmt', 'mod_settings', 'mod_vehicle_mgmt', 'mod_address_mgmt', 'mod_notifications', 'mod_workflows'].forEach(m => activeModules.add(m));
    ['page_wf_takeaway', 'page_wf_dinein', 'page_wf_online', 'page_posb_cash', 'page_posb_card', 'page_posb_upi', 'page_posd_home', 'page_posd_front', 'page_posd_bal', 'page_tm_table', 'page_tm_add', 'page_tm_book', 'page_km_kitchen', 'page_km_add', 'page_km_active', 'page_cm_list', 'page_cm_create', 'page_cm_group', 'page_im_list', 'page_im_create', 'page_im_grplist', 'page_im_variant', 'page_im_ingred', 'page_im_combo', 'page_im_toppings', 'page_im_hidden', 'page_pm_sup', 'page_pm_grplist', 'page_pm_items', 'page_pm_order', 'page_pm_rect', 'page_pm_inv', 'page_pm_rep', 'page_hr_addemp', 'page_hr_empdesig', 'page_hr_empdepts', 'page_hr_emptypes', 'page_hr_emplist', 'page_hr_empdet', 'page_hr_att', 'page_hr_salslip', 'page_hr_lvtype', 'page_hr_schmas', 'page_hr_users', 'page_hr_hollist', 'page_rep_inv', 'page_rep_sal', 'page_rep_kanban', 'page_rep_trip', 'page_mb_tenants', 'page_mb_comp', 'page_mb_branch', 'page_mb_compdet', 'page_role_perm', 'page_set_sys', 'page_set_email', 'page_set_print', 'page_set_bkp', 'page_set_work', 'page_set_doc', 'page_set_tax', 'page_set_imp', 'page_veh_mgmt', 'page_trk_act', 'page_add_struc', 'page_notif_main', 'page_notif_voice'].forEach(p => activePages.add(p));
  } else {
    const planItems = subscriptionPlansData[view] || [];
    const items = planItems.items ? planItems.items : planItems;
    if (Array.isArray(items)) {
      items.forEach(item => { 
        if (item.id && (item.id.startsWith('page_') || item.type === 'page')) {
          activePages.add(item.id);
          // Auto-detect parent module based on prefix
          if (item.id.startsWith('page_wf_')) activeModules.add('mod_workflows');
          else if (item.id.startsWith('page_posd_')) activeModules.add('mod_pos_dashboard');
          else if (item.id.startsWith('page_posb_')) activeModules.add('mod_pos_billing');
          else if (item.id.startsWith('page_tm_')) activeModules.add('mod_table_mgmt');
          else if (item.id.startsWith('page_km_')) activeModules.add('mod_kitchen_mgmt');
          else if (item.id.startsWith('page_cm_')) activeModules.add('mod_customer_mgmt');
          else if (item.id.startsWith('page_im_')) activeModules.add('mod_inventory_mgmt');
          else if (item.id.startsWith('page_pm_')) activeModules.add('mod_purchase_mgmt');
          else if (item.id.startsWith('page_hr_')) activeModules.add('mod_hr_payroll');
          else if (item.id.startsWith('page_rep_')) activeModules.add('mod_reports');
          else if (item.id.startsWith('page_mb_')) activeModules.add('mod_multi_branch');
          else if (item.id.startsWith('page_role_')) activeModules.add('mod_role_mgmt');
          else if (item.id.startsWith('page_set_')) activeModules.add('mod_settings');
          else if (item.id.startsWith('page_veh_')) activeModules.add('mod_vehicle_mgmt');
          else if (item.id.startsWith('page_add_')) activeModules.add('mod_address_mgmt');
          else if (item.id.startsWith('page_notif_')) activeModules.add('mod_notifications');
        } else {
          activeModules.add(item.id); 
          if (item.pages && Array.isArray(item.pages)) {
            item.pages.forEach(p => activePages.add(p.id));
          }
        }
      });
    }
    purchasedAddons.forEach(addon => {
      if (addon.moduleId) activeModules.add(addon.moduleId);
      if (addon.pageId) {
        activePages.add(addon.pageId);
      } else if (addon.pages && Array.isArray(addon.pages)) {
        addon.pages.forEach(p => activePages.add(p.id));
      }
    });
  }

  let flow = `flowchart TD
    %% Styling
    classDef default fill:#f8fafc,stroke:#94a3b8,stroke-width:2px,color:#0f172a,font-weight:bold;
    classDef startEnd fill:#f472b6,stroke:#831843,stroke-width:2px,color:#111827,font-weight:bold;
    classDef process fill:#60a5fa,stroke:#1e3a8a,stroke-width:2px,color:#111827,font-weight:bold;
    classDef decision fill:#fcd34d,stroke:#78350f,stroke-width:2px,color:#111827,font-weight:bold;
    classDef db fill:#34d399,stroke:#064e3b,stroke-width:2px,color:#111827,font-weight:bold;
    classDef module fill:#c084fc,stroke:#4c1d95,stroke-width:2px,color:#111827,font-weight:bold;
    classDef subTab fill:#fef08a,stroke:#854d0e,stroke-width:2px,color:#111827,font-weight:bold;

    Start((Start)):::startEnd --> Login[Login Page]
    Login --> Auth{Authentication}
    Auth -->|Success| AdminDash[Admin Dashboard]

    AdminDash --> Setup[Setup / Config]
    Setup --> ManageComp[Manage Company]
    ManageComp --> ManageBranch[Manage Branch]
    ManageBranch --> ConfigSet[Configure Settings]
    ConfigSet --> POSDashMain

    AdminDash --> AdminModules[[Admin Modules Container]]:::module
`;

  if (activeModules.has('mod_pos_billing') || activeModules.has('mod_pos_dashboard')) flow += `    AdminModules --> POSBilling["POS Billing<br/>(Core billing interface)"]\n`;
  if (activeModules.has('mod_table_mgmt')) flow += `    AdminModules --> TableMgmt["Table Management<br/>(Handle dine-in layouts)"]\n`;
  if (activeModules.has('mod_kitchen_mgmt')) flow += `    AdminModules --> KitchMgmt["Kitchen Management<br/>(KOT tracking & display)"]\n`;
  if (activeModules.has('mod_customer_mgmt')) flow += `    AdminModules --> CustMgmt["Customer Mgmt<br/>(Loyalty & CRM)"]\n`;
  if (activeModules.has('mod_inventory_mgmt')) flow += `    AdminModules --> InvMgmt["Inventory Mgmt<br/>(Stock & Recipes)"]\n`;
  if (activeModules.has('mod_purchase_mgmt')) flow += `    AdminModules --> PurchMgmt["Purchase Mgmt<br/>(Suppliers & POs)"]\n`;
  if (activeModules.has('mod_hr_payroll')) flow += `    AdminModules --> HRMgmt["HR & Payroll<br/>(Staff & Attendance)"]\n`;
  if (activeModules.has('mod_reports')) flow += `    AdminModules --> RepMgmt["Reports<br/>(Analytics & Insights)"]\n`;
  if (activeModules.has('mod_multi_branch')) flow += `    AdminModules --> BranchMgmt["Multi Branch<br/>(Tenant & Branch Config)"]\n`;
  if (activeModules.has('mod_role_mgmt')) flow += `    AdminModules --> RoleMgmt["Role Management<br/>(Permissions)"]\n`;
  if (activeModules.has('mod_settings')) flow += `    AdminModules --> SetMgmt["Settings<br/>(Global configuration)"]\n`;
  if (activeModules.has('mod_vehicle_mgmt')) flow += `    AdminModules --> VehMgmt["Vehicle Mgmt<br/>(Delivery tracking)"]\n`;
  if (activeModules.has('mod_address_mgmt')) flow += `    AdminModules --> AddrMgmt["Address Mgmt<br/>(Delivery Zones)"]\n`;
  if (activeModules.has('mod_notifications')) flow += `    AdminModules --> NotifMgmt["Notifications<br/>(Voice & Alerts)"]\n`;

  if (activeModules.has('mod_pos_billing') || activeModules.has('mod_pos_dashboard')) {
    if (activePages.has('page_posd_home') || activeModules.size === 0) flow += `    POSBilling --> POSHome[Home]:::subTab\n`;
    if (activePages.has('page_posd_front') || activeModules.size === 0) flow += `    POSBilling --> POSFront[Front Page]:::subTab\n`;
    if (activePages.has('page_posd_bal') || activeModules.size === 0) flow += `    POSBilling --> POSBal[POS Balance]:::subTab\n`;
    flow += `    POSBilling --> POSMainFlow(Proceed to Core POS Flow)\n`;
  }
  if (activeModules.has('mod_table_mgmt')) {
    if (activePages.has('page_tm_table') || activeModules.size === 0) flow += `    TableMgmt --> TPage[Table Page]:::subTab\n`;
    if (activePages.has('page_tm_add') || activeModules.size === 0) flow += `    TPage --> TAdd[Add Table]:::subTab\n`;
    if (activePages.has('page_tm_book') || activeModules.size === 0) flow += `    TPage --> TBook[Table Booking]:::subTab\n`;
  }
  if (activeModules.has('mod_kitchen_mgmt')) {
    if (activePages.has('page_km_kitchen') || activeModules.size === 0) flow += `    KitchMgmt --> KPage[Kitchen Page]:::subTab\n`;
    if (activePages.has('page_km_add') || activeModules.size === 0) flow += `    KitchMgmt --> KAdd[Add Kitchen]:::subTab\n`;
    if (activePages.has('page_km_active') || activeModules.size === 0) flow += `    KitchMgmt --> KActOrd[Active Orders]:::subTab\n`;
  }
  if (activeModules.has('mod_customer_mgmt')) {
    if (activePages.has('page_cm_list') || activeModules.size === 0) flow += `    CustMgmt --> CCust[Customer]:::subTab\n`;
    if (activePages.has('page_cm_create') || activeModules.size === 0) flow += `    CustMgmt --> CCreate[Create Cust]:::subTab\n`;
    if (activePages.has('page_cm_group') || activeModules.size === 0) flow += `    CustMgmt --> CGrp[Cust Group]:::subTab\n`;
  }
  if (activeModules.has('mod_inventory_mgmt')) {
    if (activePages.has('page_im_list') || activeModules.size === 0) flow += `    InvMgmt --> IItemList[Item List]:::subTab\n`;
    if (activePages.has('page_im_create') || activeModules.size === 0) flow += `    InvMgmt --> ICreateItem[Add Item / Item Card]:::subTab\n`;
    if (activePages.has('page_im_grplist') || activeModules.size === 0) flow += `    InvMgmt --> IItemGrp[Item Groups]:::subTab\n`;
    if (activePages.has('page_im_variant') || activeModules.size === 0) flow += `    InvMgmt --> IVariants[Variants]:::subTab\n`;
    if (activePages.has('page_im_ingred') || activeModules.size === 0) flow += `    InvMgmt --> INutrition[Nutrition]:::subTab\n`;
    if (activePages.has('page_im_hidden') || activeModules.size === 0) flow += `    InvMgmt --> IHidden[Hidden Items]:::subTab\n`;
    if (activePages.has('page_im_combo') || activeModules.size === 0) flow += `    InvMgmt --> ICombo[Combo Offer]:::subTab\n`;
    if (activePages.has('page_im_toppings') || activeModules.size === 0) flow += `    InvMgmt --> IToppings[Toppings]:::subTab\n`;
  }
  if (activeModules.has('mod_purchase_mgmt')) {
    if (activePages.has('page_pm_sup') || activeModules.size === 0) flow += `    PurchMgmt --> PSupp[Suppliers]:::subTab\n`;
    if (activePages.has('page_pm_grplist') || activeModules.size === 0) flow += `    PurchMgmt --> PSuppGrp[Supplier Grp]:::subTab\n`;
    if (activePages.has('page_pm_items') || activeModules.size === 0) flow += `    PurchMgmt --> PItems[Purchase Items]:::subTab\n`;
    if (activePages.has('page_pm_order') || activeModules.size === 0) flow += `    PurchMgmt --> PPurchOrd[Purchase Order]:::subTab\n`;
    if (activePages.has('page_pm_rect') || activeModules.size === 0) flow += `    PurchMgmt --> PReceipt[Receipt]:::subTab\n`;
    if (activePages.has('page_pm_inv') || activeModules.size === 0) flow += `    PurchMgmt --> PInvoice[Purchase Invoice]:::subTab\n`;
    if (activePages.has('page_pm_rep') || activeModules.size === 0) flow += `    PurchMgmt --> PReports[Purchase Reports]:::subTab\n`;
  }
  if (activeModules.has('mod_hr_payroll')) {
    if (activePages.has('page_hr_addemp') || activeModules.size === 0) flow += `    HRMgmt --> HAddEmp[Add Employee]:::subTab\n`;
    if (activePages.has('page_hr_empdesig') || activeModules.size === 0) flow += `    HRMgmt --> HEmpDesig(Designation)\n`;
    if (activePages.has('page_hr_empdepts') || activeModules.size === 0) flow += `    HRMgmt --> HEmpDept(Department)\n`;
    if (activePages.has('page_hr_emptypes') || activeModules.size === 0) flow += `    HRMgmt --> HEmpType(Employee Type)\n`;
    if (activePages.has('page_hr_emplist') || activeModules.size === 0) flow += `    HRMgmt --> HEmpList[Employee List]:::subTab\n`;
    if (activePages.has('page_hr_empdet') || activeModules.size === 0) flow += `    HRMgmt --> HEmpDet[Employee Details]:::subTab\n`;
    if (activePages.has('page_hr_att') || activeModules.size === 0) flow += `    HRMgmt --> HAttend[Attendance]:::subTab\n`;
    if (activePages.has('page_hr_salslip') || activeModules.size === 0) flow += `    HRMgmt --> HSalSlip[Salary Slip]:::subTab\n`;
    if (activePages.has('page_hr_lvtype') || activeModules.size === 0) flow += `    HRMgmt --> HLeave[Leave]:::subTab\n`;
    if (activePages.has('page_hr_schmas') || activeModules.size === 0) flow += `    HRMgmt --> HSched[Schedule]:::subTab\n`;
    if (activePages.has('page_hr_users') || activeModules.size === 0) flow += `    HRMgmt --> HUsers[Users]:::subTab\n`;
    if (activePages.has('page_hr_hollist') || activeModules.size === 0) flow += `    HRMgmt --> HHoliday[Holiday List]:::subTab\n`;
  }
  if (activeModules.has('mod_reports')) {
    if (activePages.has('page_rep_inv') || activeModules.size === 0) flow += `    RepMgmt --> RSalesInv[Sales Inv]:::subTab\n`;
    if (activePages.has('page_rep_sal') || activeModules.size === 0) flow += `    RepMgmt --> RSalesRep[Sales Rep]:::subTab\n`;
    if (activePages.has('page_rep_kanban') || activeModules.size === 0) flow += `    RepMgmt --> RSalesKan[Sales Kanban]:::subTab\n`;
    if (activePages.has('page_rep_trip') || activeModules.size === 0) flow += `    RepMgmt --> RTripRep[Trip Report]:::subTab\n`;
  }
  if (activeModules.has('mod_multi_branch')) {
    if (activePages.has('page_mb_tenants') || activeModules.size === 0) flow += `    BranchMgmt --> BTenants[Tenants]:::subTab\n`;
    if (activePages.has('page_mb_comp') || activeModules.size === 0) flow += `    BranchMgmt --> BComp[Companies]:::subTab\n`;
    if (activePages.has('page_mb_branch') || activeModules.size === 0) flow += `    BranchMgmt --> BBranches[Branches]:::subTab\n`;
    if (activePages.has('page_mb_compdet') || activeModules.size === 0) flow += `    BranchMgmt --> BCompInfo[Company Info]:::subTab\n`;
  }
  if (activeModules.has('mod_role_mgmt')) {
    if (activePages.has('page_role_perm') || activeModules.size === 0) flow += `    RoleMgmt --> RPerm[Permissions]:::subTab\n`;
  }
  if (activeModules.has('mod_settings')) {
    if (activePages.has('page_set_sys') || activeModules.size === 0) flow += `    SetMgmt --> SSysSet[System Settings]:::subTab\n`;
    if (activePages.has('page_set_email') || activeModules.size === 0) flow += `    SetMgmt --> SEmailSet[Email Settings]:::subTab\n`;
    if (activePages.has('page_set_print') || activeModules.size === 0) flow += `    SetMgmt --> SPrintSet[Print Settings]:::subTab\n`;
    if (activePages.has('page_set_bkp') || activeModules.size === 0) flow += `    SetMgmt --> SBackups[Backups]:::subTab\n`;
    if (activePages.has('page_set_work') || activeModules.size === 0) flow += `    SetMgmt --> SWorkDays[Working Days]:::subTab\n`;
    if (activePages.has('page_set_doc') || activeModules.size === 0) flow += `    SetMgmt --> SDocType[DocType Mgmt]:::subTab\n`;
    if (activePages.has('page_set_tax') || activeModules.size === 0) flow += `    SetMgmt --> STaxMast[Tax Master]:::subTab\n`;
    if (activePages.has('page_set_imp') || activeModules.size === 0) flow += `    SetMgmt --> SAdvImp[Advanced Import]:::subTab\n`;
  }
  if (activeModules.has('mod_vehicle_mgmt')) {
    if (activePages.has('page_veh_mgmt') || activeModules.size === 0) flow += `    VehMgmt --> VVehicles[Vehicles]:::subTab\n`;
    if (activePages.has('page_trk_act') || activeModules.size === 0) flow += `    VehMgmt --> VOrdTrack[Order Tracking]:::subTab\n`;
  }
  if (activeModules.has('mod_address_mgmt')) {
    if (activePages.has('page_add_struc') || activeModules.size === 0) flow += `    AddrMgmt --> AAddress[Address]:::subTab\n`;
  }
  if (activeModules.has('mod_notifications')) {
    if (activePages.has('page_notif_main') || activeModules.size === 0) flow += `    NotifMgmt --> NNotifs[Notifications]:::subTab\n`;
    if (activePages.has('page_notif_voice') || activeModules.size === 0) flow += `    NotifMgmt --> NVoice[Voice Support]:::subTab\n`;
  }

  flow += `
    POSMainFlow --> POSDashMain[POS Dashboard]
    POSDashMain --> OpenEntry[Opening Entry]:::process
    OpenEntry --> HomeFront[Home / Front Page]
    HomeFront --> OrderExp{Select Order Experience}
`;

  if (activePages.has('page_wf_takeaway') || activeModules.size === 0) flow += `    OrderExp -->|Takeaway| CustSelect\n`;
  if (activePages.has('page_wf_dinein')) flow += `    OrderExp -->|Dine-In| SelTable[Select Table]\n    SelTable --> CustSelect\n`;
  if (activePages.has('page_wf_online')) flow += `    OrderExp -->|Online Delivery| CustSelect\n`;

  flow += `
    CustSelect[Select / Create Customer] --> SearchItems[Search Items]
    SearchItems --> AddCart[Add Items to Cart]
    AddCart --> AddToppings[Add Toppings / Combo]
`;

  if (activeModules.has('mod_kitchen_mgmt')) {
    flow += `
    AddToppings --> NeedsKitchen{Require Kitchen Prep?}
    
    %% Kitchen Route
    NeedsKitchen -->|Yes| SaveOrder[Save Active Order]
    SaveOrder --> SendKitchen[Send Order to Kitchen]
    SendKitchen --> KitchenDash[Kitchen Dashboard]:::process
    KitchenDash --> PendOrder[Pending Orders]
    PendOrder --> AccOrder[Accept Order]
    AccOrder --> PrepStatus[Preparing Status]
    PrepStatus --> ReadyStatus[Ready Status]
    ReadyStatus --> NotifyPOS[Notify POS Dashboard]
    NotifyPOS --> ServeCust[Serve Customer]
    ServeCust --> Payment
    
    %% Direct Checkout Route (No Kitchen)
    NeedsKitchen -->|No / Fast Checkout| Payment
`;
  } else {
    flow += `    AddToppings --> Payment\n`;
  }

  flow += `    Payment{Select Payment Method}\n`;
  
  if (activeModules.has('mod_pos_billing')) {
    if (activePages.has('page_posb_cash') || activeModules.size === 0) flow += `    POSBilling --> PCash[Cash Payment]:::subTab\n    Payment -->|Cash| PCash\n    PCash --> GenInvoice\n`;
    if (activePages.has('page_posb_card') || activeModules.size === 0) flow += `    POSBilling --> PCard[Card Payment]:::subTab\n    Payment -->|Card| PCard\n    PCard --> GenInvoice\n`;
    if (activePages.has('page_posb_upi') || activeModules.size === 0) flow += `    POSBilling --> PUPI[UPI Payment]:::subTab\n    Payment -->|UPI| PUPI\n    PUPI --> GenInvoice\n`;
  } else {
    flow += `    Payment --> GenInvoice\n`;
  }

  flow += `
    GenInvoice[Generate Invoice] --> SalesEntry[Sales Entry Created]:::db
    SalesEntry --> RepUpdate[Reports Updated]:::db
    
    RepUpdate -.-> RepMgmt
    RepUpdate --> CloseEntryTab[Closing Entry]:::subTab
    CloseEntryTab --> DayEnd[Day End Completed]
    DayEnd --> End((End)):::startEnd
  `;

  return flow;
};

const MASTER_MODULES_LIST = [
  {
    id: 'mod_workflows', name: 'Workflows & Experience', price: 40, isExpanded: false,
    pages: [
      { id: 'page_wf_takeaway', name: 'Takeaway', price: 0 },
      { id: 'page_wf_dinein', name: 'Dine In', price: 15 },
      { id: 'page_wf_online', name: 'Online Delivery', price: 25 }
    ]
  },
  {
    id: 'mod_pos_dashboard', name: 'POS Dashboard', price: 0, isExpanded: false,
    pages: [
      { id: 'page_posd_home', name: 'Home Dashboard', price: 0 },
      { id: 'page_posd_front', name: 'Front Page', price: 0 },
      { id: 'page_posd_bal', name: 'POS Balance', price: 0 }
    ]
  },
  {
    id: 'mod_pos_billing', name: 'POS Billing', price: 0, isExpanded: false,
    pages: [
      { id: 'page_posb_open', name: 'Opening Entry', price: 0 },
      { id: 'page_posb_close', name: 'Closing Entry', price: 0 },
      { id: 'page_posb_cash', name: 'Cash Payment', price: 0 },
      { id: 'page_posb_card', name: 'Card Payment', price: 0 },
      { id: 'page_posb_upi', name: 'UPI Payment', price: 0 }
    ]
  },
  {
    id: 'mod_table_mgmt', name: 'Table Management', price: 25, isExpanded: false,
    pages: [
      { id: 'page_tm_table', name: 'Table Page', price: 0 },
      { id: 'page_tm_add', name: 'Add Table', price: 0 },
      { id: 'page_tm_book', name: 'Table Booking', price: 0 }
    ]
  },
  {
    id: 'mod_kitchen_mgmt', name: 'Kitchen Management', price: 35, isExpanded: false,
    pages: [
      { id: 'page_km_kitchen', name: 'Kitchen Page', price: 0 },
      { id: 'page_km_add', name: 'Add Kitchen', price: 0 },
      { id: 'page_km_active', name: 'Active Orders', price: 0 }
    ]
  },
  {
    id: 'mod_customer_mgmt', name: 'Customer Management', price: 15, isExpanded: false,
    pages: [
      { id: 'page_cm_list', name: 'Customer List', price: 0 },
      { id: 'page_cm_create', name: 'Create Customer', price: 0 },
      { id: 'page_cm_group', name: 'Customer Group', price: 0 }
    ]
  },
  {
    id: 'mod_inventory_mgmt', name: 'Inventory Management', price: 20, isExpanded: false,
    pages: [
      { id: 'page_im_list', name: 'Items List', price: 0 },
      { id: 'page_im_create', name: 'Create Item', price: 0 },
      { id: 'page_im_hidden', name: 'Hidden Items', price: 0 },
      { id: 'page_im_grplist', name: 'Item Group List', price: 0 },
      { id: 'page_im_addgrp', name: 'Add Item Group', price: 0 },
      { id: 'page_im_variant', name: 'Create Variant', price: 0 },
      { id: 'page_im_ingred', name: 'Add Ingredients & Nutrition', price: 0 },
      { id: 'page_im_combo', name: 'Combo Offer', price: 0 },
      { id: 'page_im_toppings', name: 'Toppings', price: 0 }
    ]
  },
  {
    id: 'mod_purchase_mgmt', name: 'Purchase Management', price: 20, isExpanded: false,
    pages: [
      { id: 'page_pm_sup', name: 'Suppliers', price: 0 },
      { id: 'page_pm_grplist', name: 'Supplier Group List', price: 0 },
      { id: 'page_pm_items', name: 'Items List', price: 0 },
      { id: 'page_pm_order', name: 'Purchase Order', price: 0 },
      { id: 'page_pm_rect', name: 'Purchase Receipt', price: 0 },
      { id: 'page_pm_inv', name: 'Purchase Invoice', price: 0 },
      { id: 'page_pm_rep', name: 'Purchase Reports', price: 0 }
    ]
  },
  {
    id: 'mod_hr_payroll', name: 'HR & Payroll', price: 175, isExpanded: false,
    pages: [
      { id: 'page_hr_addemp', name: 'Add Employee', price: 0 },
      { id: 'page_hr_emplist', name: 'Employee List', price: 0 },
      { id: 'page_hr_delpersons', name: 'Delivery Persons', price: 0 },
      { id: 'page_delivery_panel', name: 'Delivery Panel', price: 0 },
      { id: 'page_hr_empdet', name: 'Employee Details', price: 0 },
      { id: 'page_hr_empdesig', name: 'Employee Designations', price: 0 },
      { id: 'page_hr_emptypes', name: 'Employee Types', price: 0 },
      { id: 'page_hr_empdepts', name: 'Employee Departments', price: 0 },
      { id: 'page_hr_att', name: 'Attendance', price: 0 },
      { id: 'page_hr_attview', name: 'Attendance View', price: 0 },
      { id: 'page_hr_salslip', name: 'Salary Slip', price: 0 },
      { id: 'page_hr_salrect', name: 'Salary Receipt List', price: 0 },
      { id: 'page_hr_lvtype', name: 'Leave Type', price: 0 },
      { id: 'page_hr_lvalloc', name: 'Leave Allocation', price: 0 },
      { id: 'page_hr_lvapply', name: 'Leave Apply', price: 0 },
      { id: 'page_hr_schmas', name: 'Schedule Master', price: 0 },
      { id: 'page_hr_schass', name: 'Schedule Assign Employee', price: 0 },
      { id: 'page_hr_roaster', name: 'Roaster', price: 0 },
      { id: 'page_hr_users', name: 'Users', price: 0 },
      { id: 'page_hr_hollist', name: 'Holiday List', price: 0 },
      { id: 'page_hr_extdoc', name: 'Extended DocType', price: 0 }
    ]
  },
  {
    id: 'mod_vehicle_mgmt', name: 'Vehicle Management', price: 15, isExpanded: false,
    pages: [
      { id: 'page_veh_mgmt', name: 'Vehicle Management', price: 0 }
    ]
  },
  {
    id: 'mod_address_mgmt', name: 'Address Management', price: 10, isExpanded: false,
    pages: [
      { id: 'page_add_struc', name: 'Address Structure', price: 0 }
    ]
  },
  {
    id: 'mod_reports', name: 'Reports', price: 15, isExpanded: false,
    pages: [
      { id: 'page_rep_inv', name: 'Sales Invoice', price: 0 },
      { id: 'page_rep_sal', name: 'Sales Report', price: 0 },
      { id: 'page_rep_kanban', name: 'Sales Kanban', price: 0 },
      { id: 'page_rep_trip', name: 'Trip Report', price: 0 }
    ]
  },
  {
    id: 'mod_settings', name: 'Settings', price: 15, isExpanded: false,
    pages: [
      { id: 'page_set_sys', name: 'System Settings', price: 0 },
      { id: 'page_set_email', name: 'Email Settings', price: 0 },
      { id: 'page_set_print', name: 'Print Settings', price: 0 },
      { id: 'page_set_bkp', name: 'Backups', price: 0 },
      { id: 'page_set_imp', name: 'Advanced Import', price: 0 },
      { id: 'page_set_work', name: 'Working Days', price: 0 },
      { id: 'page_set_doc', name: 'DocType Management', price: 0 },
      { id: 'page_set_tax', name: 'Tax Master', price: 0 }
    ]
  },
  {
    id: 'mod_multi_branch', name: 'Multi Branch Support', price: 50, isExpanded: false,
    pages: [
      { id: 'page_mb_tenants', name: 'Manage Tenants', price: 0 },
      { id: 'page_mb_comp', name: 'Manage Companies', price: 0 },
      { id: 'page_mb_branch', name: 'Manage Branches', price: 0 },
      { id: 'page_mb_compdet', name: 'Company Details', price: 0 }
    ]
  },
  {
    id: 'mod_auth', name: 'Authentication', price: 10, isExpanded: false,
    pages: [
      { id: 'page_auth_log', name: 'Login', price: 0 },
      { id: 'page_auth_reg', name: 'Register', price: 0 }
    ]
  },
  {
    id: 'mod_role_mgmt', name: 'Role Management', price: 15, isExpanded: false,
    pages: [
      { id: 'page_role_perm', name: 'Role Permissions', price: 0 }
    ]
  },
  {
    id: 'mod_notifications', name: 'Notifications & Voice', price: 30, isExpanded: false,
    pages: [
      { id: 'page_notif_main', name: 'Notifications', price: 0 },
      { id: 'page_notif_voice', name: 'Voice Support', price: 0 }
    ]
  },
  {
    id: 'mod_order_tracking', name: 'Order Tracking', price: 15, isExpanded: false,
    pages: [
      { id: 'page_trk_act', name: 'Active Orders', price: 0 }
    ]
  },
  {
    id: 'mod_admin_dashboard', name: 'Admin Dashboard', price: 25, isExpanded: false,
    pages: [
      { id: 'page_ad_dash', name: 'Dashboard', price: 0 },
      { id: 'page_ad_pan', name: 'Admin Panel', price: 0 },
      { id: 'page_ad_main', name: 'Main Page', price: 0 }
    ]
  }
];

const countryMeta = {
  "Afghanistan": { phone_code: "+93", phone_length: 9, currency: "AFN", currency_symbol: "؋" },
  "Albania": { phone_code: "+355", phone_length: 9, currency: "ALL", currency_symbol: "L" },
  "Algeria": { phone_code: "+213", phone_length: 9, currency: "DZD", currency_symbol: "دج" },
  "Andorra": { phone_code: "+376", phone_length: 6, currency: "EUR", currency_symbol: "€" },
  "Angola": { phone_code: "+244", phone_length: 9, currency: "AOA", currency_symbol: "Kz" },
  "Antigua and Barbuda": { phone_code: "+1268", phone_length: 7, currency: "XCD", currency_symbol: "$" },
  "Argentina": { phone_code: "+54", phone_length: 10, currency: "ARS", currency_symbol: "$" },
  "Armenia": { phone_code: "+374", phone_length: 8, currency: "AMD", currency_symbol: "֏" },
  "Australia": { phone_code: "+61", phone_length: 9, currency: "AUD", currency_symbol: "$" },
  "Austria": { phone_code: "+43", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "Azerbaijan": { phone_code: "+994", phone_length: 9, currency: "AZN", currency_symbol: "₼" },
  "Bahamas": { phone_code: "+1242", phone_length: 7, currency: "BSD", currency_symbol: "$" },
  "Bahrain": { phone_code: "+973", phone_length: 8, currency: "BHD", currency_symbol: ".د.ب" },
  "Bangladesh": { phone_code: "+880", phone_length: 10, currency: "BDT", currency_symbol: "৳" },
  "Barbados": { phone_code: "+1246", phone_length: 7, currency: "BBD", currency_symbol: "$" },
  "Belarus": { phone_code: "+375", phone_length: 9, currency: "BYN", currency_symbol: "Br" },
  "Belgium": { phone_code: "+32", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Belize": { phone_code: "+501", phone_length: 7, currency: "BZD", currency_symbol: "BZ$" },
  "Benin": { phone_code: "+229", phone_length: 8, currency: "XOF", currency_symbol: "CFA" },
  "Bhutan": { phone_code: "+975", phone_length: 8, currency: "BTN", currency_symbol: "Nu." },
  "Bolivia": { phone_code: "+591", phone_length: 8, currency: "BOB", currency_symbol: "Bs." },
  "Bosnia and Herzegovina": { phone_code: "+387", phone_length: 8, currency: "BAM", currency_symbol: "KM" },
  "Botswana": { phone_code: "+267", phone_length: 8, currency: "BWP", currency_symbol: "P" },
  "Brazil": { phone_code: "+55", phone_length: 11, currency: "BRL", currency_symbol: "R$" },
  "Brunei": { phone_code: "+673", phone_length: 7, currency: "BND", currency_symbol: "$" },
  "Bulgaria": { phone_code: "+359", phone_length: 9, currency: "BGN", currency_symbol: "лв" },
  "Burkina Faso": { phone_code: "+226", phone_length: 8, currency: "XOF", currency_symbol: "CFA" },
  "Burundi": { phone_code: "+257", phone_length: 8, currency: "BIF", currency_symbol: "FBu" },
  "Cambodia": { phone_code: "+855", phone_length: 9, currency: "KHR", currency_symbol: "៛" },
  "Cameroon": { phone_code: "+237", phone_length: 9, currency: "XAF", currency_symbol: "FCFA" },
  "Canada": { phone_code: "+1", phone_length: 10, currency: "CAD", currency_symbol: "$" },
  "Cape Verde": { phone_code: "+238", phone_length: 7, currency: "CVE", currency_symbol: "$" },
  "Central African Republic": { phone_code: "+236", phone_length: 8, currency: "XAF", currency_symbol: "FCFA" },
  "Chad": { phone_code: "+235", phone_length: 8, currency: "XAF", currency_symbol: "FCFA" },
  "Chile": { phone_code: "+56", phone_length: 9, currency: "CLP", currency_symbol: "$" },
  "China": { phone_code: "+86", phone_length: 11, currency: "CNY", currency_symbol: "¥" },
  "Colombia": { phone_code: "+57", phone_length: 10, currency: "COP", currency_symbol: "$" },
  "Comoros": { phone_code: "+269", phone_length: 7, currency: "KMF", currency_symbol: "CF" },
  "Costa Rica": { phone_code: "+506", phone_length: 8, currency: "CRC", currency_symbol: "₡" },
  "Croatia": { phone_code: "+385", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Cuba": { phone_code: "+53", phone_length: 8, currency: "CUP", currency_symbol: "$" },
  "Cyprus": { phone_code: "+357", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Czech Republic": { phone_code: "+420", phone_length: 9, currency: "CZK", currency_symbol: "Kč" },
  "Denmark": { phone_code: "+45", phone_length: 8, currency: "DKK", currency_symbol: "kr" },
  "Djibouti": { phone_code: "+253", phone_length: 8, currency: "DJF", currency_symbol: "Fdj" },
  "Dominica": { phone_code: "+1767", phone_length: 7, currency: "XCD", currency_symbol: "$" },
  "Dominican Republic": { phone_code: "+1809", phone_length: 10, currency: "DOP", currency_symbol: "RD$" },
  "Ecuador": { phone_code: "+593", phone_length: 9, currency: "USD", currency_symbol: "$" },
  "Egypt": { phone_code: "+20", phone_length: 10, currency: "EGP", currency_symbol: "E£" },
  "El Salvador": { phone_code: "+503", phone_length: 8, currency: "USD", currency_symbol: "$" },
  "Equatorial Guinea": { phone_code: "+240", phone_length: 9, currency: "XAF", currency_symbol: "FCFA" },
  "Eritrea": { phone_code: "+291", phone_length: 7, currency: "ERN", currency_symbol: "Nfk" },
  "Estonia": { phone_code: "+372", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Eswatini": { phone_code: "+268", phone_length: 8, currency: "SZL", currency_symbol: "E" },
  "Ethiopia": { phone_code: "+251", phone_length: 9, currency: "ETB", currency_symbol: "Br" },
  "Fiji": { phone_code: "+679", phone_length: 7, currency: "FJD", currency_symbol: "$" },
  "Finland": { phone_code: "+358", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "France": { phone_code: "+33", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Gabon": { phone_code: "+241", phone_length: 8, currency: "XAF", currency_symbol: "FCFA" },
  "Gambia": { phone_code: "+220", phone_length: 7, currency: "GMD", currency_symbol: "D" },
  "Georgia": { phone_code: "+995", phone_length: 9, currency: "GEL", currency_symbol: "₾" },
  "Germany": { phone_code: "+49", phone_length: 11, currency: "EUR", currency_symbol: "€" },
  "Ghana": { phone_code: "+233", phone_length: 9, currency: "GHS", currency_symbol: "₵" },
  "Greece": { phone_code: "+30", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "Grenada": { phone_code: "+1473", phone_length: 7, currency: "XCD", currency_symbol: "$" },
  "Guatemala": { phone_code: "+502", phone_length: 8, currency: "GTQ", currency_symbol: "Q" },
  "Guinea": { phone_code: "+224", phone_length: 9, currency: "GNF", currency_symbol: "FG" },
  "Guinea-Bissau": { phone_code: "+245", phone_length: 7, currency: "XOF", currency_symbol: "CFA" },
  "Guyana": { phone_code: "+592", phone_length: 7, currency: "GYD", currency_symbol: "$" },
  "Haiti": { phone_code: "+509", phone_length: 8, currency: "HTG", currency_symbol: "G" },
  "Honduras": { phone_code: "+504", phone_length: 8, currency: "HNL", currency_symbol: "L" },
  "Hungary": { phone_code: "+36", phone_length: 9, currency: "HUF", currency_symbol: "Ft" },
  "Iceland": { phone_code: "+354", phone_length: 7, currency: "ISK", currency_symbol: "kr" },
  "India": { phone_code: "+91", phone_length: 10, currency: "INR", currency_symbol: "₹" },
  "Indonesia": { phone_code: "+62", phone_length: 10, currency: "IDR", currency_symbol: "Rp" },
  "Iran": { phone_code: "+98", phone_length: 10, currency: "IRR", currency_symbol: "﷼" },
  "Iraq": { phone_code: "+964", phone_length: 10, currency: "IQD", currency_symbol: "ع.د" },
  "Ireland": { phone_code: "+353", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Israel": { phone_code: "+972", phone_length: 9, currency: "ILS", currency_symbol: "₪" },
  "Italy": { phone_code: "+39", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "Jamaica": { phone_code: "+1876", phone_length: 7, currency: "JMD", currency_symbol: "J$" },
  "Japan": { phone_code: "+81", phone_length: 10, currency: "JPY", currency_symbol: "¥" },
  "Jordan": { phone_code: "+962", phone_length: 9, currency: "JOD", currency_symbol: "د.ا" },
  "Kazakhstan": { phone_code: "+7", phone_length: 10, currency: "KZT", currency_symbol: "₸" },
  "Kenya": { phone_code: "+254", phone_length: 9, currency: "KES", currency_symbol: "KSh" },
  "Kiribati": { phone_code: "+686", phone_length: 8, currency: "AUD", currency_symbol: "$" },
  "Kuwait": { phone_code: "+965", phone_length: 8, currency: "KWD", currency_symbol: "د.ك" },
  "Kyrgyzstan": { phone_code: "+996", phone_length: 9, currency: "KGS", currency_symbol: "сом" },
  "Laos": { phone_code: "+856", phone_length: 8, currency: "LAK", currency_symbol: "₭" },
  "Latvia": { phone_code: "+371", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Lebanon": { phone_code: "+961", phone_length: 8, currency: "LBP", currency_symbol: "ل.ل" },
  "Lesotho": { phone_code: "+266", phone_length: 8, currency: "LSL", currency_symbol: "L" },
  "Liberia": { phone_code: "+231", phone_length: 8, currency: "LRD", currency_symbol: "$" },
  "Libya": { phone_code: "+218", phone_length: 9, currency: "LYD", currency_symbol: "ل.د" },
  "Liechtenstein": { phone_code: "+423", phone_length: 7, currency: "CHF", currency_symbol: "CHF" },
  "Lithuania": { phone_code: "+370", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Luxembourg": { phone_code: "+352", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Madagascar": { phone_code: "+261", phone_length: 9, currency: "MGA", currency_symbol: "Ar" },
  "Malawi": { phone_code: "+265", phone_length: 9, currency: "MWK", currency_symbol: "MK" },
  "Malaysia": { phone_code: "+60", phone_length: 9, currency: "MYR", currency_symbol: "RM" },
  "Maldives": { phone_code: "+960", phone_length: 7, currency: "MVR", currency_symbol: "Rf" },
  "Mali": { phone_code: "+223", phone_length: 8, currency: "XOF", currency_symbol: "CFA" },
  "Malta": { phone_code: "+356", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Marshall Islands": { phone_code: "+692", phone_length: 7, currency: "USD", currency_symbol: "$" },
  "Mauritania": { phone_code: "+222", phone_length: 8, currency: "MRU", currency_symbol: "UM" },
  "Mauritius": { phone_code: "+230", phone_length: 8, currency: "MUR", currency_symbol: "₨" },
  "Mexico": { phone_code: "+52", phone_length: 10, currency: "MXN", currency_symbol: "$" },
  "Micronesia": { phone_code: "+691", phone_length: 7, currency: "USD", currency_symbol: "$" },
  "Moldova": { phone_code: "+373", phone_length: 8, currency: "MDL", currency_symbol: "L" },
  "Monaco": { phone_code: "+377", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Mongolia": { phone_code: "+976", phone_length: 8, currency: "MNT", currency_symbol: "₮" },
  "Montenegro": { phone_code: "+382", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Morocco": { phone_code: "+212", phone_length: 9, currency: "MAD", currency_symbol: "د.م." },
  "Mozambique": { phone_code: "+258", phone_length: 9, currency: "MZN", currency_symbol: "MT" },
  "Myanmar": { phone_code: "+95", phone_length: 9, currency: "MMK", currency_symbol: "Ks" },
  "Namibia": { phone_code: "+264", phone_length: 9, currency: "NAD", currency_symbol: "$" },
  "Nauru": { phone_code: "+674", phone_length: 7, currency: "AUD", currency_symbol: "$" },
  "Nepal": { phone_code: "+977", phone_length: 10, currency: "NPR", currency_symbol: "₨" },
  "Netherlands": { phone_code: "+31", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "New Zealand": { phone_code: "+64", phone_length: 9, currency: "NZD", currency_symbol: "$" },
  "Nicaragua": { phone_code: "+505", phone_length: 8, currency: "NIO", currency_symbol: "C$" },
  "Niger": { phone_code: "+227", phone_length: 8, currency: "XOF", currency_symbol: "CFA" },
  "Nigeria": { phone_code: "+234", phone_length: 10, currency: "NGN", currency_symbol: "₦" },
  "North Korea": { phone_code: "+850", phone_length: 10, currency: "KPW", currency_symbol: "₩" },
  "North Macedonia": { phone_code: "+389", phone_length: 8, currency: "MKD", currency_symbol: "ден" },
  "Norway": { phone_code: "+47", phone_length: 8, currency: "NOK", currency_symbol: "kr" },
  "Oman": { phone_code: "+968", phone_length: 8, currency: "OMR", currency_symbol: "﷼" },
  "Pakistan": { phone_code: "+92", phone_length: 10, currency: "PKR", currency_symbol: "₨" },
  "Palau": { phone_code: "+680", phone_length: 7, currency: "USD", currency_symbol: "$" },
  "Panama": { phone_code: "+507", phone_length: 8, currency: "PAB", currency_symbol: "B/." },
  "Papua New Guinea": { phone_code: "+675", phone_length: 8, currency: "PGK", currency_symbol: "K" },
  "Paraguay": { phone_code: "+595", phone_length: 9, currency: "PYG", currency_symbol: "₲" },
  "Peru": { phone_code: "+51", phone_length: 9, currency: "PEN", currency_symbol: "S/" },
  "Philippines": { phone_code: "+63", phone_length: 10, currency: "PHP", currency_symbol: "₱" },
  "Poland": { phone_code: "+48", phone_length: 9, currency: "PLN", currency_symbol: "zł" },
  "Portugal": { phone_code: "+351", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Qatar": { phone_code: "+974", phone_length: 8, currency: "QAR", currency_symbol: "﷼" },
  "Romania": { phone_code: "+40", phone_length: 9, currency: "RON", currency_symbol: "lei" },
  "Russia": { phone_code: "+7", phone_length: 10, currency: "RUB", currency_symbol: "₽" },
  "Rwanda": { phone_code: "+250", phone_length: 9, currency: "RWF", currency_symbol: "FRw" },
  "Saint Lucia": { phone_code: "+1758", phone_length: 7, currency: "XCD", currency_symbol: "$" },
  "Samoa": { phone_code: "+685", phone_length: 7, currency: "WST", currency_symbol: "T" },
  "San Marino": { phone_code: "+378", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "Saudi Arabia": { phone_code: "+966", phone_length: 9, currency: "SAR", currency_symbol: "﷼" },
  "Senegal": { phone_code: "+221", phone_length: 9, currency: "XOF", currency_symbol: "CFA" },
  "Serbia": { phone_code: "+381", phone_length: 9, currency: "RSD", currency_symbol: "дин." },
  "Seychelles": { phone_code: "+248", phone_length: 7, currency: "SCR", currency_symbol: "₨" },
  "Sierra Leone": { phone_code: "+232", phone_length: 8, currency: "SLE", currency_symbol: "Le" },
  "Singapore": { phone_code: "+65", phone_length: 8, currency: "SGD", currency_symbol: "$" },
  "Slovakia": { phone_code: "+421", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Slovenia": { phone_code: "+386", phone_length: 8, currency: "EUR", currency_symbol: "€" },
  "Solomon Islands": { phone_code: "+677", phone_length: 7, currency: "SBD", currency_symbol: "$" },
  "Somalia": { phone_code: "+252", phone_length: 8, currency: "SOS", currency_symbol: "Sh" },
  "South Africa": { phone_code: "+27", phone_length: 9, currency: "ZAR", currency_symbol: "R" },
  "South Korea": { phone_code: "+82", phone_length: 10, currency: "KRW", currency_symbol: "₩" },
  "South Sudan": { phone_code: "+211", phone_length: 9, currency: "SSP", currency_symbol: "£" },
  "Spain": { phone_code: "+34", phone_length: 9, currency: "EUR", currency_symbol: "€" },
  "Sri Lanka": { phone_code: "+94", phone_length: 9, currency: "LKR", currency_symbol: "Rs" },
  "Sudan": { phone_code: "+249", phone_length: 9, currency: "SDG", currency_symbol: "ج.س." },
  "Suriname": { phone_code: "+597", phone_length: 7, currency: "SRD", currency_symbol: "$" },
  "Sweden": { phone_code: "+46", phone_length: 9, currency: "SEK", currency_symbol: "kr" },
  "Switzerland": { phone_code: "+41", phone_length: 9, currency: "CHF", currency_symbol: "CHF" },
  "Syria": { phone_code: "+963", phone_length: 9, currency: "SYP", currency_symbol: "£" },
  "Taiwan": { phone_code: "+886", phone_length: 9, currency: "TWD", currency_symbol: "NT$" },
  "Tajikistan": { phone_code: "+992", phone_length: 9, currency: "TJS", currency_symbol: "ЅМ" },
  "Tanzania": { phone_code: "+255", phone_length: 9, currency: "TZS", currency_symbol: "Sh" },
  "Thailand": { phone_code: "+66", phone_length: 9, currency: "THB", currency_symbol: "฿" },
  "Togo": { phone_code: "+228", phone_length: 8, currency: "XOF", currency_symbol: "CFA" },
  "Tonga": { phone_code: "+676", phone_length: 7, currency: "TOP", currency_symbol: "T$" },
  "Trinidad and Tobago": { phone_code: "+1868", phone_length: 7, currency: "TTD", currency_symbol: "TT$" },
  "Tunisia": { phone_code: "+216", phone_length: 8, currency: "TND", currency_symbol: "د.ت" },
  "Turkey": { phone_code: "+90", phone_length: 10, currency: "TRY", currency_symbol: "₺" },
  "Turkmenistan": { phone_code: "+993", phone_length: 8, currency: "TMT", currency_symbol: "m" },
  "Tuvalu": { phone_code: "+688", phone_length: 6, currency: "AUD", currency_symbol: "$" },
  "Uganda": { phone_code: "+256", phone_length: 9, currency: "UGX", currency_symbol: "USh" },
  "Ukraine": { phone_code: "+380", phone_length: 9, currency: "UAH", currency_symbol: "₴" },
  "United Arab Emirates": { phone_code: "+971", phone_length: 9, currency: "AED", currency_symbol: "د.إ" },
  "United Kingdom": { phone_code: "+44", phone_length: 10, currency: "GBP", currency_symbol: "£" },
  "United States": { phone_code: "+1", phone_length: 10, currency: "USD", currency_symbol: "$" },
  "Uruguay": { phone_code: "+598", phone_length: 8, currency: "UYU", currency_symbol: "$U" },
  "Uzbekistan": { phone_code: "+998", phone_length: 9, currency: "UZS", currency_symbol: "so'm" },
  "Vanuatu": { phone_code: "+678", phone_length: 7, currency: "VUV", currency_symbol: "VT" },
  "Vatican City": { phone_code: "+379", phone_length: 10, currency: "EUR", currency_symbol: "€" },
  "Venezuela": { phone_code: "+58", phone_length: 10, currency: "VES", currency_symbol: "Bs." },
  "Vietnam": { phone_code: "+84", phone_length: 9, currency: "VND", currency_symbol: "₫" },
  "Yemen": { phone_code: "+967", phone_length: 9, currency: "YER", currency_symbol: "﷼" },
  "Zambia": { phone_code: "+260", phone_length: 9, currency: "ZMW", currency_symbol: "ZK" },
  "Zimbabwe": { phone_code: "+263", phone_length: 9, currency: "ZWL", currency_symbol: "$" }
};

const countryHierarchy = {
  "Afghanistan": ["Province", "District", "Area"],
  "Albania": ["County", "Municipality", "Area"],
  "Algeria": ["Province", "District", "Area"],
  "Andorra": ["Parish", "Area", "N/A"],
  "Angola": ["Province", "Municipality", "Area"],
  "Antigua and Barbuda": ["Parish", "Area", "N/A"],
  "Argentina": ["Province", "Department", "Municipality"],
  "Armenia": ["Province", "Community", "Area"],
  "Australia": ["State/Territory", "Local Government Area", "Suburb"],
  "Austria": ["State", "District", "Municipality"],
  "Azerbaijan": ["Economic Region", "District", "Area"],
  "Bahamas": ["District", "Area", "N/A"],
  "Bahrain": ["Governorate", "Municipality", "Area"],
  "Bangladesh": ["Division", "District", "Upazila"],
  "Barbados": ["Parish", "Area", "N/A"],
  "Belarus": ["Region", "District", "Area"],
  "Belgium": ["Region", "Province", "Municipality"],
  "Belize": ["District", "Town", "Area"],
  "Benin": ["Department", "Commune", "Area"],
  "Bhutan": ["District", "Gewog", "Village"],
  "Bolivia": ["Department", "Province", "Municipality"],
  "Bosnia and Herzegovina": ["Entity", "Canton", "Municipality"],
  "Botswana": ["District", "Sub-District", "Area"],
  "Brazil": ["State", "Municipality", "Neighborhood"],
  "Brunei": ["District", "Mukim", "Village"],
  "Bulgaria": ["Province", "Municipality", "Area"],
  "Burkina Faso": ["Region", "Province", "Commune"],
  "Burundi": ["Province", "Commune", "Area"],
  "Cambodia": ["Province", "District", "Commune"],
  "Cameroon": ["Region", "Division", "Sub-Division"],
  "Canada": ["Province/Territory", "Municipality", "Area"],
  "Cape Verde": ["Municipality", "Area", "N/A"],
  "Central African Republic": ["Prefecture", "Sub-Prefecture", "Area"],
  "Chad": ["Province", "Department", "Area"],
  "Chile": ["Region", "Province", "Commune"],
  "China": ["Province", "Prefecture", "County"],
  "Colombia": ["Department", "Municipality", "Area"],
  "Comoros": ["Island", "Prefecture", "Area"],
  "Costa Rica": ["Province", "Canton", "District"],
  "Croatia": ["County", "Municipality", "Area"],
  "Cuba": ["Province", "Municipality", "Area"],
  "Cyprus": ["District", "Municipality", "Area"],
  "Czech Republic": ["Region", "District", "Municipality"],
  "Denmark": ["Region", "Municipality", "Area"],
  "Djibouti": ["Region", "District", "Area"],
  "Dominica": ["Parish", "Area", "N/A"],
  "Dominican Republic": ["Province", "Municipality", "Area"],
  "Ecuador": ["Province", "Canton", "Parish"],
  "Egypt": ["Governorate", "District", "Area"],
  "El Salvador": ["Department", "Municipality", "Area"],
  "Equatorial Guinea": ["Province", "District", "Area"],
  "Eritrea": ["Region", "Sub-Region", "Area"],
  "Estonia": ["County", "Municipality", "Area"],
  "Eswatini": ["Region", "Inkhundla", "Area"],
  "Ethiopia": ["Region", "Zone", "Woreda"],
  "Fiji": ["Division", "Province", "District"],
  "Finland": ["Region", "Municipality", "Area"],
  "France": ["Region", "Department", "Commune"],
  "Gabon": ["Province", "Department", "Area"],
  "Gambia": ["Region", "District", "Area"],
  "Georgia": ["Region", "Municipality", "Area"],
  "Germany": ["State", "District", "Municipality"],
  "Ghana": ["Region", "District", "Area"],
  "Greece": ["Region", "Municipality", "Area"],
  "Grenada": ["Parish", "Area", "N/A"],
  "Guatemala": ["Department", "Municipality", "Area"],
  "Guinea": ["Region", "Prefecture", "Sub-Prefecture"],
  "Guinea-Bissau": ["Region", "Sector", "Area"],
  "Guyana": ["Region", "Neighborhood Council", "Area"],
  "Haiti": ["Department", "Arrondissement", "Commune"],
  "Honduras": ["Department", "Municipality", "Area"],
  "Hungary": ["County", "District", "Municipality"],
  "Iceland": ["Region", "Municipality", "Area"],
  "India": ["State/UT", "District", "Taluk"],
  "Indonesia": ["Province", "Regency/City", "District"],
  "Iran": ["Province", "County", "District"],
  "Iraq": ["Governorate", "District", "Area"],
  "Ireland": ["County", "Municipality", "Area"],
  "Israel": ["District", "Sub-District", "Area"],
  "Italy": ["Region", "Province", "Municipality"],
  "Jamaica": ["Parish", "Area", "N/A"],
  "Japan": ["Prefecture", "City/Ward", "District"],
  "Jordan": ["Governorate", "District", "Area"],
  "Kazakhstan": ["Region", "District", "Area"],
  "Kenya": ["County", "Sub-County", "Ward"],
  "Kiribati": ["Island", "Council", "Area"],
  "Kuwait": ["Governorate", "Area", "Block"],
  "Kyrgyzstan": ["Region", "District", "Area"],
  "Laos": ["Province", "District", "Village"],
  "Latvia": ["Municipality", "Area", "N/A"],
  "Lebanon": ["Governorate", "District", "Area"],
  "Lesotho": ["District", "Community Council", "Area"],
  "Liberia": ["County", "District", "Area"],
  "Libya": ["District", "Municipality", "Area"],
  "Liechtenstein": ["Municipality", "Area", "N/A"],
  "Lithuania": ["County", "Municipality", "Area"],
  "Luxembourg": ["Canton", "Commune", "Area"],
  "Madagascar": ["Region", "District", "Commune"],
  "Malawi": ["Region", "District", "Area"],
  "Malaysia": ["State", "District", "Mukim"],
  "Maldives": ["Atoll", "Island", "Area"],
  "Mali": ["Region", "Cercle", "Commune"],
  "Malta": ["Region", "Local Council", "Area"],
  "Marshall Islands": ["Atoll", "Municipality", "Area"],
  "Mauritania": ["Region", "Department", "Area"],
  "Mauritius": ["District", "Village", "Area"],
  "Mexico": ["State", "Municipality", "Locality"],
  "Micronesia": ["State", "Municipality", "Area"],
  "Moldova": ["District", "Commune", "Area"],
  "Monaco": ["Commune", "Area", "N/A"],
  "Mongolia": ["Province", "District", "Bag"],
  "Montenegro": ["Municipality", "Area", "N/A"],
  "Morocco": ["Region", "Province", "Commune"],
  "Mozambique": ["Province", "District", "Area"],
  "Myanmar": ["Region/State", "District", "Township"],
  "Namibia": ["Region", "Constituency", "Area"],
  "Nauru": ["District", "Area", "N/A"],
  "Nepal": ["Province", "District", "Municipality"],
  "Netherlands": ["Province", "Municipality", "Area"],
  "New Zealand": ["Region", "District", "Area"],
  "Nicaragua": ["Department", "Municipality", "Area"],
  "Niger": ["Region", "Department", "Commune"],
  "Nigeria": ["State", "Local Government Area", "Ward"],
  "North Korea": ["Province", "County", "Area"],
  "North Macedonia": ["Municipality", "Area", "N/A"],
  "Norway": ["County", "Municipality", "Area"],
  "Oman": ["Governorate", "Wilayat", "Area"],
  "Pakistan": ["Province", "Division", "District"],
  "Palau": ["State", "Area", "N/A"],
  "Panama": ["Province", "District", "Corregimiento"],
  "Papua New Guinea": ["Province", "District", "Area"],
  "Paraguay": ["Department", "District", "Area"],
  "Peru": ["Region", "Province", "District"],
  "Philippines": ["Region", "Province", "City/Municipality"],
  "Poland": ["Voivodeship", "County", "Gmina"],
  "Portugal": ["District", "Municipality", "Parish"],
  "Qatar": ["Municipality", "Zone", "Area"],
  "Romania": ["County", "Municipality", "Area"],
  "Russia": ["Federal Subject", "District", "Municipality"],
  "Rwanda": ["Province", "District", "Sector"],
  "Saint Lucia": ["District", "Area", "N/A"],
  "Samoa": ["District", "Village", "Area"],
  "San Marino": ["Municipality", "Area", "N/A"],
  "Saudi Arabia": ["Province", "Governorate", "Area"],
  "Senegal": ["Region", "Department", "Arrondissement"],
  "Serbia": ["District", "Municipality", "Area"],
  "Seychelles": ["District", "Area", "N/A"],
  "Sierra Leone": ["Province", "District", "Area"],
  "Singapore": ["City-State", "N/A", "N/A"],
  "Slovakia": ["Region", "District", "Municipality"],
  "Slovenia": ["Statistical Region", "Municipality", "Area"],
  "Solomon Islands": ["Province", "Ward", "Area"],
  "Somalia": ["State", "District", "Area"],
  "South Africa": ["Province", "District", "Municipality"],
  "South Korea": ["Province", "City/County", "District"],
  "South Sudan": ["State", "County", "Payam"],
  "Spain": ["Autonomous Community", "Province", "Municipality"],
  "Sri Lanka": ["Province", "District", "Division"],
  "Sudan": ["State", "Locality", "Area"],
  "Suriname": ["District", "Resort", "Area"],
  "Sweden": ["County", "Municipality", "Area"],
  "Switzerland": ["Canton", "Municipality", "Area"],
  "Syria": ["Governorate", "District", "Area"],
  "Taiwan": ["County/City", "District", "Area"],
  "Tajikistan": ["Region", "District", "Area"],
  "Tanzania": ["Region", "District", "Ward"],
  "Thailand": ["Province", "District", "Sub-District"],
  "Togo": ["Region", "Prefecture", "Canton"],
  "Tonga": ["Division", "District", "Area"],
  "Trinidad and Tobago": ["Region", "Municipality", "Area"],
  "Tunisia": ["Governorate", "Delegation", "Sector"],
  "Turkey": ["Province", "District", "Neighborhood"],
  "Turkmenistan": ["Province", "District", "Area"],
  "Tuvalu": ["Island", "Area", "N/A"],
  "Uganda": ["Region", "District", "Sub-County"],
  "Ukraine": ["Oblast", "Raion", "Hromada"],
  "United Arab Emirates": ["Emirate", "City", "Area"],
  "United Kingdom": ["Country", "County", "Borough"],
  "United States": ["State", "County", "City"],
  "Uruguay": ["Department", "Municipality", "Area"],
  "Uzbekistan": ["Region", "District", "Area"],
  "Vanuatu": ["Province", "Municipality", "Area"],
  "Vatican City": ["None", "N/A", "N/A"],
  "Venezuela": ["State", "Municipality", "Parish"],
  "Vietnam": ["Province", "District", "Commune"],
  "Yemen": ["Governorate", "District", "Area"],
  "Zambia": ["Province", "District", "Area"],
  "Zimbabwe": ["Province", "District", "Area"]
};



const OnboardOrganization = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editTenantId = searchParams.get('tenantId');

  const [step, setStep] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('warning');
  const [successPopup, setSuccessPopup] = useState(false);
  const [isEditMode, setIsEditMode] = useState(!!editTenantId);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [showAddons, setShowAddons] = useState(false);

  // Verification Portal State
  const [emailForOtp, setEmailForOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [verifiedToken, setVerifiedToken] = useState('');

  // Step 2: Survey States
  const [survey, setSurvey] = useState({
    operation_type: 'QSR',
    goal: 'Speed up billing & checkout',
    daily_orders: '50 - 200',
    dine_in: 'Yes'
  });

  const [showPassword, setShowPassword] = useState({
    tenant: false,
    companies: {},
    branches: {}
  });

  const toggleTenantPassword = () => setShowPassword(p => ({ ...p, tenant: !p.tenant }));
  const toggleCompanyPassword = (id) => setShowPassword(p => ({ ...p, companies: { ...p.companies, [id]: !p.companies[id] } }));
  const toggleBranchPassword = (id) => setShowPassword(p => ({ ...p, branches: { ...p.branches, [id]: !p.branches[id] } }));

  // Step 3: Tenant Details
  const [selectedBillingDuration, setSelectedBillingDuration] = useState(1);
  const [tenant, setTenant] = useState({
    tenant_name: '',
    country: '',
    state: '',
    city: '',
    address: '',
    phone: '',
    email: location.state?.verifiedEmail || '',
    subscription_plan: 'Basic',
    admin_email: location.state?.verifiedEmail || '',
    admin_username: '',
    admin_password: ''
  });

  const [companies, setCompanies] = useState([
    { id: 1, company_name: '', company_code: '', phone: '', email: '', address: '', country: '', state: '', city: '', admin_email: '', admin_password: '' }
  ]);

  const [branches, setBranches] = useState([
    { id: 1, company_name: '', branch_name: '', branch_code: '', phone: '', email: '', address: '', country: '', state: '', city: '', admin_email: '', admin_password: '' }
  ]);

  // Checkboxes
  const [hasMultipleCompanies, setHasMultipleCompanies] = useState(false);
  const [hasBranches, setHasBranches] = useState(false);

  // Step 4: Modules Toggles
  const [modules, setModules] = useState({
    items: true,
    sales: true,
    purchase: true,
    kot: true,
    delivery: true,
    tables: true
  });

  const [complianceDocs, setComplianceDocs] = useState([
    { doc_type: 'Trade License', trade_name: '', reg_number: '', expiry_date: '', file: null, file_name: '' },
    { doc_type: 'GST / VAT Certificate', trade_name: '', reg_number: '', expiry_date: '', file: null, file_name: '' },
    { doc_type: 'FSSAI License', trade_name: '', reg_number: '', expiry_date: '', file: null, file_name: '' },
    { doc_type: 'Accreditation Certificate', trade_name: '', reg_number: '', expiry_date: '', file: null, file_name: '' }
  ]);

  const [subscriptionPlansData, setSubscriptionPlansData] = useState({});
  const [purchasedAddons, setPurchasedAddons] = useState([]);
  const [addonModal, setAddonModal] = useState({ isOpen: false, module: null, page: null, planName: '', missingDependencies: [] });
  const [flowchartView, setFlowchartView] = useState('All');

  const getMissingDependencies = (itemId) => {
    let missing = [];
    let queue = [itemId];
    let visited = new Set();

    const findItemDetails = (id, type) => {
      for (const planName of Object.keys(subscriptionPlansData)) {
        const planObj = subscriptionPlansData[planName];
        const items = planObj && planObj.items ? planObj.items : planObj || [];
        for (const mod of items) {
          if (type === 'module' && mod.id === id) return { ...mod, type: 'module' };
          if (type === 'page' && mod.pages) {
            const page = mod.pages.find(p => p.id === id);
            if (page) return { ...page, type: 'page', parentModuleId: mod.id };
          }
        }
      }
      return null;
    };

    while (queue.length > 0) {
      let currentId = queue.shift();
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      const deps = BUILDER_DEPENDENCIES[currentId];
      if (deps) {
        deps.forEach(dep => {
          const targetId = dep.pageId || dep.moduleId;
          const targetType = dep.pageId ? 'page' : 'module';

          if (!visited.has(targetId)) {
            const basePlanFullName = (tenant.subscription_plan || 'Basic') + ' Plan';
            const planObj = subscriptionPlansData[basePlanFullName];
            const basePlanItems = planObj && planObj.items ? planObj.items : planObj || [];

            let isOwnedInBase = false;
            if (targetType === 'module') {
              isOwnedInBase = basePlanItems.some(bItem => bItem.id === targetId);
            } else {
              isOwnedInBase = basePlanItems.some(bItem => bItem.pages && bItem.pages.some(pg => pg.id === targetId));
            }

            const isOwnedInAddons = targetType === 'module'
              ? purchasedAddons.some(a => a.moduleId === targetId && !a.pageId)
              : purchasedAddons.some(a => a.pageId === targetId);

            if (!isOwnedInBase && !isOwnedInAddons) {
              const details = findItemDetails(targetId, targetType);
              if (details) {
                if (!missing.some(m => m.id === details.id)) {
                  missing.push({
                    id: details.id,
                    name: details.name,
                    price: Number(details.price || 0),
                    type: targetType,
                    moduleId: targetType === 'module' ? details.id : details.parentModuleId,
                    pageId: targetType === 'page' ? details.id : null,
                    pages: targetType === 'module' ? details.pages : [],
                    checked: true
                  });
                }
              }
              queue.push(targetId);
            }
          }
        });
      }
    }
    return missing;
  };

  const openAddonModal = (module, page, planName) => {
    const triggerId = page ? page.id : module.id;
    const missing = getMissingDependencies(triggerId);
    setAddonModal({
      isOpen: true,
      module,
      page,
      planName,
      missingDependencies: missing
    });
  };

  useEffect(() => {
    const fetchPlanConfigs = async () => {
      try {
        const res = await fetch('/api/admin/plan_config');
        if (res.ok) {
          const data = await res.json();
          setSubscriptionPlansData(data);
        }
      } catch (err) {
        console.error("Failed to load plans", err);
      }
    };
    fetchPlanConfigs();
  }, []);

  useEffect(() => {
    if (editTenantId && editTenantId !== 'null' && editTenantId !== 'undefined') {
      setIsEditMode(true);
      const targetStep = searchParams.get('step');
      if (targetStep) {
        setStep(parseInt(targetStep, 10));
      } else {
        setStep(3); // Skip OTP and Survey in Edit mode
      }
      fetchEditData();
    }
  }, [editTenantId]); // deliberately omitted searchParams to avoid looping

  // Auto configure modules based on plan selection & dine-in survey choice
  useEffect(() => {
    let defaults = { items: true, sales: true, purchase: false, kot: false, delivery: false, tables: false };

    if (tenant.subscription_plan === 'Silver') {
      defaults = { items: true, sales: true, purchase: true, kot: true, delivery: false, tables: true };
    } else if (tenant.subscription_plan === 'Gold') {
      defaults = { items: true, sales: true, purchase: true, kot: true, delivery: true, tables: true };
    }

    // Survey restriction: if no table dinning, disable tables and kot modules
    if (survey.dine_in === 'No') {
      defaults.tables = false;
      defaults.kot = false;
    }

    setModules(defaults);
  }, [tenant.subscription_plan, survey.dine_in]);

  const fetchEditData = async () => {
    setLoading(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await axios.get(`/api/onboard-organization/${editTenantId}`, { headers });
      const data = res.data;

      if (data.tenant) {
        setTenant({
          ...data.tenant,
          admin_email: data.tenant.admin_email || data.tenant.email || '',
          admin_username: data.tenant.admin_username || '',
          admin_password: data.tenant.plain_password || '••••••••'
        });
        if (data.tenant.billing_cycle) {
          const bc = data.tenant.billing_cycle;
          if (bc === 'Monthly') {
            setSelectedBillingDuration(1);
          } else {
            const parsed = parseInt(bc.split(' ')[0], 10);
            if (!isNaN(parsed)) setSelectedBillingDuration(parsed);
          }
        }
        if (data.tenant.modules_enabled) {
          const modMap = {};
          data.tenant.modules_enabled.forEach(m => modMap[m] = true);
          setModules(prev => ({ ...prev, ...modMap }));
        }
        if (data.tenant.compliance_docs) {
          // Merge with template
          setComplianceDocs(prev => prev.map(p => {
            const found = data.tenant.compliance_docs.find(d => d.doc_type === p.doc_type);
            return found ? { ...p, ...found } : p;
          }));
        }
        if (data.tenant.purchased_addons) {
          setPurchasedAddons(data.tenant.purchased_addons);
        }
      }

      if (data.companies && data.companies.length > 0) {
        const mappedCompanies = data.companies.map(c => ({
          ...c,
          id: c._id || c.id || Date.now() + Math.random(),
          country: c.country || '',
          state: c.state || '',
          city: c.city || '',
          address: c.address || '',
          admin_email: c.admin_email || '',
          admin_password: c.plain_password || '••••••••'
        }));
        setCompanies(mappedCompanies);
        // Show multi-company panel if there's more than one, OR if the first company differs from tenant name
        const isMultiComp = data.companies.length > 1 || (data.companies.length === 1 && data.companies[0].company_name !== data.tenant?.tenant_name);
        setHasMultipleCompanies(isMultiComp);
      }

      if (data.branches && data.branches.length > 0) {
        setBranches(data.branches.map(b => {
          const comp = data.companies ? data.companies.find(c => c._id === b.company_id || c.id === b.company_id) : null;
          return {
            ...b,
            id: b._id || b.id || Date.now() + Math.random(),
            company_name: b.company_name || (comp ? comp.company_name : ''),
            country: b.country || '',
            state: b.state || '',
            city: b.city || '',
            address: b.address || '',
            admin_email: b.admin_email || '',
            admin_password: b.plain_password || '••••••••'
          };
        }));
        setHasBranches(true);
      }
    } catch (err) {
      setError(`Failed to load organization data: ${err.response?.data?.error || err.message} (ID: ${editTenantId})`);
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!emailForOtp) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/request-otp', { email: emailForOtp });
      setOtpSent(true);
      alert(`OTP sent! (For testing: ${res.data.debug_otp})`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/verify-otp', { email: emailForOtp, otp: otpCode });
      setVerifiedToken(res.data.token);
      setTenant(prev => ({ ...prev, email: emailForOtp, admin_email: emailForOtp }));
      setStep(2); // Go to profiling
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => setStep(s => s + 1);
  const handlePrev = () => setStep(s => s - 1);

  const handleFileChange = (index, file) => {
    setComplianceDocs(prev => prev.map((item, i) => i === index ? { ...item, file } : item));
  };

  const updateComplianceField = (index, field, value) => {
    setComplianceDocs(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const handleSubmit = async (e, overrideAddons = null, overridePlan = null) => {
    if (e && e.preventDefault) e.preventDefault();
    setLoading(true);
    setError('');

    const showError = (msg) => {
      setWarningMessage(msg);
      setWarningType('warning');
      setLoading(false);
    };

    // Frontend validation
    if (!tenant.tenant_name) return showError('Missing required detail: Organization Name (Step 3)');
    if (!tenant.admin_email) return showError('Missing required detail: Super Admin Email (Step 3)');
    if (!tenant.admin_password) return showError('Missing required detail: Super Admin Password (Step 3)');

    let finalCompsToSubmit = [];
    if (!hasMultipleCompanies) {
      const existingMainComp = companies.length > 0 ? companies[0] : null;
      finalCompsToSubmit = [{
        _id: existingMainComp && existingMainComp.id ? existingMainComp.id : undefined,
        company_name: tenant.tenant_name,
        company_code: "MAIN",
        phone: tenant.phone,
        email: tenant.email,
        address: tenant.address,
        is_default: true,
        country: tenant.country,
        state: tenant.state,
        city: tenant.city,
        admin_email: tenant.admin_email,
        admin_password: tenant.admin_password
      }];
    } else {
      finalCompsToSubmit = companies.filter(c => c.company_name && c.company_name.trim() !== '');
    }

    for (const c of finalCompsToSubmit) {
      if (!c.admin_email) return showError(`Missing Admin Email for Company: ${c.company_name}`);
      if (!c.admin_password) return showError(`Missing Admin Password for Company: ${c.company_name}`);
    }

    let finalBranchesToSubmit = [];
    if (hasBranches) {
      finalBranchesToSubmit = branches.filter(b => b.branch_name && b.branch_name.trim() !== '' && b.company_name && b.company_name.trim() !== '');
      for (const b of finalBranchesToSubmit) {
        if (!b.admin_email) return showError(`Missing Admin Email for Branch: ${b.branch_name}`);
        if (!b.admin_password) return showError(`Missing Admin Password for Branch: ${b.branch_name}`);
      }
    }

    try {
      const formPayload = new FormData();

      const payloadTenant = {
        ...tenant,
        subscription_plan: overridePlan !== null ? overridePlan : tenant.subscription_plan,
        currency: tenant.country && countryMeta[tenant.country] ? countryMeta[tenant.country].currency : '',
        currency_symbol: tenant.country && countryMeta[tenant.country] ? countryMeta[tenant.country].currency_symbol : ''
      };

      if (searchParams.get('step') === '4') {
        delete payloadTenant.admin_email;
        delete payloadTenant.admin_username;
        delete payloadTenant.admin_password;
      }

      const enrichedComps = finalCompsToSubmit.map(c => ({
        ...c,
        currency: c.country && countryMeta[c.country] ? countryMeta[c.country].currency : '',
        currency_symbol: c.country && countryMeta[c.country] ? countryMeta[c.country].currency_symbol : ''
      }));

      const enrichedBranches = finalBranchesToSubmit.map(b => ({
        ...b,
        currency: b.country && countryMeta[b.country] ? countryMeta[b.country].currency : '',
        currency_symbol: b.country && countryMeta[b.country] ? countryMeta[b.country].currency_symbol : ''
      }));
      formPayload.append('tenant', JSON.stringify(payloadTenant));
      formPayload.append('billing_cycle', selectedBillingDuration === 1 ? 'Monthly' : selectedBillingDuration + ' Months');

      formPayload.append('companies', JSON.stringify(enrichedComps));
      formPayload.append('branches', JSON.stringify(enrichedBranches));

      formPayload.append('survey_responses', JSON.stringify(survey));

      const enabledModulesList = Object.keys(modules).filter(k => modules[k]);
      formPayload.append('modules_enabled', JSON.stringify(enabledModulesList));
      const activeAddons = overrideAddons !== null ? overrideAddons : purchasedAddons;
      formPayload.append('purchased_addons', JSON.stringify(activeAddons));

      const finalDocs = complianceDocs.map((doc, idx) => {
        if (doc.file) {
          const fieldName = `compliance_file_${idx}`;
          formPayload.append(fieldName, doc.file);
          return {
            doc_type: doc.doc_type,
            reg_number: doc.reg_number,
            expiry_date: doc.expiry_date,
            file_field_name: fieldName
          };
        }
        return {
          doc_type: doc.doc_type,
          reg_number: doc.reg_number,
          expiry_date: doc.expiry_date,
          file_name: doc.file_name
        };
      });
      formPayload.append('compliance_docs', JSON.stringify(finalDocs));

      const headers = { 'Content-Type': 'multipart/form-data' };
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      let res;
      if (isEditMode) {
        res = await axios.put(`/api/onboard-organization/${editTenantId}`, formPayload, { headers });
      } else {
        res = await axios.post('/api/onboard-organization', formPayload, { headers });
      }

      if (res.status === 201 || res.status === 200) {
        setSuccessPopup(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred during setup');
    } finally {
      setLoading(false);
    }
  };

  const addCompany = () => setCompanies([...companies, { id: Date.now(), company_name: '', company_code: '', phone: '', email: '', address: '', country: '', state: '', city: '', admin_email: '', admin_password: '' }]);
  const removeCompany = (id) => setCompanies(companies.filter(c => c.id !== id));

  const addBranch = () => setBranches([...branches, { id: Date.now(), company_name: '', branch_name: '', branch_code: '', phone: '', email: '', address: '', country: '', state: '', city: '', admin_email: '', admin_password: '' }]);
  const removeBranch = (id) => setBranches(branches.filter(b => b.id !== id));

  const updateCompany = (id, field, value) => {
    setCompanies(companies.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const updateBranch = (id, field, value) => {
    setBranches(branches.map(b => b.id === id ? { ...b, [field]: value } : b));
  };

  const handlePurchaseAddon = () => {
    if (!addonModal.module) return;

    let finalAddons = [];
    setPurchasedAddons(prev => {
      let updated = [...prev];

      const safeAdd = (item) => {
        const exists = updated.find(a => a.moduleId === item.moduleId && a.pageId === item.pageId);
        if (!exists) updated.push(item);
      };

      safeAdd({
        moduleId: addonModal.module.id,
        moduleName: addonModal.module.name,
        pageId: addonModal.page ? addonModal.page.id : null,
        pageName: addonModal.page ? addonModal.page.name : null,
        price: addonModal.page ? Number(addonModal.page.price || 0) : Number(addonModal.module.price || 0),
        sourcePlan: addonModal.planName,
        pages: addonModal.page ? [] : (addonModal.module.pages || [])
      });

      if (addonModal.missingDependencies) {
        addonModal.missingDependencies.forEach(dep => {
          if (dep.checked) {
            safeAdd({
              moduleId: dep.moduleId,
              moduleName: dep.type === 'module' ? dep.name : 'Dependency',
              pageId: dep.pageId,
              pageName: dep.type === 'page' ? dep.name : null,
              price: Number(dep.price || 0),
              sourcePlan: 'Auto-Dependency',
              pages: dep.pages || []
            });
          }
        });
      }

      finalAddons = updated;
      return updated;
    });

    setAddonModal({ isOpen: false, module: null, page: null, planName: '', missingDependencies: [] });
  };

  const removeAddon = (idx) => {
    setPurchasedAddons(prev => {
      const itemToRemove = prev[idx];
      const triggerName = itemToRemove.pageName || itemToRemove.moduleName;
      const bundlePrefix = 'Bundled with ' + triggerName;

      // Remove the clicked item AND any bundled items associated with it
      return prev.filter((item, i) => i !== idx && item.sourcePlan !== bundlePrefix);
    });
  };

  const styles = {
    wrapper: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      boxSizing: 'border-box'
    },
    card: {
      background: '#1e293b',
      border: '1px solid #334155',
      width: '100%',
      maxWidth: (step === 4 && showAddons) ? '1250px' : '850px',
      borderRadius: '24px',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
      overflow: 'hidden',
      position: 'relative',
      transition: 'max-width 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    header: {
      background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
      padding: '40px',
      color: '#ffffff',
      textAlign: 'center'
    },
    headerTitle: {
      margin: 0,
      fontSize: '2rem',
      fontWeight: '800',
      letterSpacing: '-0.5px'
    },
    headerSubtitle: {
      margin: '10px 0 0 0',
      fontSize: '1.1rem',
      opacity: 0.9,
      fontWeight: '500'
    },
    progressContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '25px',
      gap: '8px'
    },
    progressDot: (active) => ({
      width: active ? '24px' : '8px',
      height: '8px',
      borderRadius: '4px',
      background: active ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
      transition: 'all 0.3s ease'
    }),
    body: {
      padding: '40px'
    },
    sectionTitle: {
      fontSize: '1.4rem',
      color: '#ffffff',
      margin: '0 0 20px 0',
      paddingBottom: '12px',
      borderBottom: '1px solid #334155',
      fontWeight: '700',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: '20px',
      marginBottom: '20px'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    label: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#94a3b8'
    },
    input: {
      padding: '12px 16px',
      borderRadius: '10px',
      border: '1px solid #475569',
      fontSize: '0.95rem',
      outline: 'none',
      color: '#f8fafc',
      backgroundColor: '#0f172a',
      transition: 'all 0.2s ease'
    },
    select: {
      padding: '12px 16px',
      borderRadius: '10px',
      border: '1px solid #475569',
      fontSize: '0.95rem',
      outline: 'none',
      color: '#f8fafc',
      backgroundColor: '#0f172a',
      cursor: 'pointer'
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '40px',
      paddingTop: '20px',
      borderTop: '1px solid #334155'
    },
    btnPrev: {
      background: '#334155',
      color: '#f8fafc',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    },
    btnNext: {
      background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
      color: '#ffffff',
      border: 'none',
      padding: '12px 32px',
      borderRadius: '10px',
      fontSize: '0.95rem',
      fontWeight: '600',
      cursor: 'pointer',
      boxShadow: '0 4px 14px rgba(99, 102, 241, 0.4)',
      transition: 'all 0.3s ease'
    },
    optionCard: (selected) => ({
      background: selected ? '#312e81' : '#0f172a',
      border: selected ? '2px solid #6366f1' : '1px solid #334155',
      padding: '20px',
      borderRadius: '16px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textAlign: 'center'
    }),
    planCard: (selected) => ({
      background: selected ? '#312e81' : '#1e293b',
      border: selected ? '2px solid #6366f1' : '1px solid #334155',
      padding: '24px',
      borderRadius: '16px',
      cursor: 'pointer',
      flex: '1 1 0',
      minWidth: 0,
      transition: 'all 0.2s ease'
    }),
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      cursor: 'pointer',
      fontSize: '0.95rem',
      color: '#cbd5e1'
    }
  };

  return (
    <div style={styles.wrapper}>
      {successPopup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(5px)' }}>
          <div style={{ background: '#1e293b', padding: '40px', borderRadius: '24px', textAlign: 'center', border: '1px solid #334155', maxWidth: '400px', width: '90%', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
            <div style={{ background: 'rgba(16, 185, 129, 0.1)', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#10b981' }}>
              <CheckCircle size={40} />
            </div>
            <h3 style={{ color: '#f8fafc', fontSize: '1.5rem', marginBottom: '10px', fontWeight: 'bold' }}>Success!</h3>
            <p style={{ color: '#94a3b8', fontSize: '1.05rem', marginBottom: '15px', lineHeight: '1.5' }}>
              {isEditMode ? 'Organization Updated Successfully!' : 'Organization Setup Successful! You can now login.'}
            </p>
            <div style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.3)', borderRadius: '12px', padding: '12px', marginBottom: '25px' }}>
              <p style={{ margin: 0, color: '#e2e8f0', fontSize: '0.95rem' }}>
                <strong style={{ color: '#818cf8' }}>Plan:</strong> {tenant.subscription_plan ? (tenant.subscription_plan.endsWith('Plan') ? tenant.subscription_plan : `${tenant.subscription_plan} Plan`) : 'Basic Plan'}
                {purchasedAddons.length > 0 && <span> <br/><strong style={{ color: '#818cf8' }}>Add-ons:</strong> {purchasedAddons.length}</span>}
              </p>
            </div>
            <button 
              onClick={() => {
                setSuccessPopup(false);
                if (!isEditMode) navigate('/login');
                else navigate(-1);
              }}
              style={{ background: '#6366f1', color: '#fff', border: 'none', padding: '12px 30px', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', width: '100%', transition: 'background 0.2s' }}
              onMouseOver={e => e.currentTarget.style.background = '#4f46e5'}
              onMouseOut={e => e.currentTarget.style.background = '#6366f1'}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {showFlowModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.95)', zIndex: 100000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1e293b', borderBottom: '1px solid #334155' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
              <h3 style={{ color: '#f8fafc', margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>System Flow Diagram</h3>
              <div style={{ display: 'flex', gap: '10px', background: '#0f172a', padding: '5px', borderRadius: '10px', border: '1px solid #334155' }}>
                {['All', 'Basic Plan', 'Silver Plan', 'Gold Plan'].map(view => (
                  <button 
                    key={view}
                    onClick={() => setFlowchartView(view)}
                    style={{
                      background: flowchartView === view ? '#3b82f6' : 'transparent',
                      color: flowchartView === view ? '#ffffff' : '#94a3b8',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {view}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={() => setShowFlowModal(false)} style={{ background: '#ef4444', color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem' }}>Close</button>
          </div>
          <div style={{ flex: 1, padding: '20px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <MermaidChart chart={generateSystemFlowchart(flowchartView, purchasedAddons, subscriptionPlansData)} />
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        input:focus, select:focus {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2) !important;
        }
        .mermaid-container svg {
          max-width: none !important;
          width: auto !important;
          height: auto !important;
        }
      `}</style>

      {warningMessage && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          animation: 'fadeIn 0.2s ease-out'
        }}>
          <div className="premium-modal-box" style={{
            background: '#ffffff',
            borderRadius: '24px',
            padding: '40px 32px',
            width: '90%',
            maxWidth: '440px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            animation: 'modalScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background glowing accent */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '200px',
              height: '200px',
              background: warningType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)',
              zIndex: 0,
              pointerEvents: 'none'
            }}></div>

            {/* Icon Container */}
            <div className="premium-modal-icon" style={{
              width: '84px',
              height: '84px',
              borderRadius: '50%',
              background: warningType === 'success' ? '#ecfdf5' : '#fffbeb',
              color: warningType === 'success' ? '#06D6A0' : '#FF9F04',
              border: `2px solid ${warningType === 'success' ? '#a7f3d0' : '#fde68a'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '38px',
              marginBottom: '24px',
              boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)',
              zIndex: 1,
              animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
            }}>
              {warningType === 'success' ? <FaUserCheck /> : <FaExclamationTriangle />}
            </div>

            {/* Title */}
            <h3 style={{
              fontSize: '24px',
              fontWeight: '800',
              color: '#1e293b',
              margin: '0 0 12px 0',
              zIndex: 1,
              fontFamily: `'Gilroy', 'Inter', sans-serif`
            }}>
              {warningType === 'success' ? "Success" : "Notice"}
            </h3>

            {/* Message */}
            <p style={{
              fontSize: '16px',
              color: '#64748b',
              lineHeight: '1.6',
              margin: '0 0 32px 0',
              zIndex: 1,
              fontWeight: '500'
            }}>
              {warningMessage}
            </p>

            {/* Action Buttons */}
            <div className="premium-modal-actions" style={{
              display: 'flex',
              gap: '16px',
              width: '100%',
              zIndex: 1
            }}>
              <button 
                type="button"
                className="premium-btn-cancel" 
                onClick={() => setWarningMessage("")}
                style={{
                  flex: 1,
                  height: '48px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  fontFamily: `'Gilroy', 'Inter', sans-serif`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="premium-btn-ok" 
                onClick={() => {
                  setWarningMessage("");
                  if (warningType === 'success') {
                    if (searchParams.get('step') === '4') {
                      navigate(-1);
                    } else {
                      navigate(isEditMode ? '/manage-tenants' : '/login');
                    }
                  }
                }} 
                style={{
                  flex: 1,
                  height: '48px',
                  background: warningType === 'success' ? '#06D6A0' : '#604BE8',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontWeight: '700',
                  fontSize: '15px',
                  cursor: 'pointer',
                  boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)',
                  transition: 'all 0.2s ease',
                  fontFamily: `'Gilroy', 'Inter', sans-serif`
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 12px 25px -5px rgba(6, 214, 160, 0.4)' : '0 12px 25px -5px rgba(96, 75, 232, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'; }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.headerTitle}>{isEditMode ? 'Edit SaaS Organization' : 'SaaS Organization Setup'}</h2>
          <p style={styles.headerSubtitle}>{isEditMode ? 'Modify plan and company settings' : 'Start your SaaS restaurant operation wizard'}</p>
          <button 
            onClick={() => setShowFlowModal(true)} 
            style={{ marginTop: '20px', background: '#10b981', color: '#fff', padding: '10px 20px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', boxShadow: '0 4px 10px rgba(16,185,129,0.3)' }}
          >
            View Flow Diagram
          </button>
          <div style={styles.progressContainer}>
            <div style={styles.progressDot(step === 2)}></div>
            <div style={styles.progressDot(step === 3)}></div>
            <div style={styles.progressDot(step === 4)}></div>
            <div style={styles.progressDot(step === 5)}></div>
          </div>
        </div>

        <div style={styles.body}>
          {error && (
            <div style={{ background: '#7f1d1d', border: '1px solid #f87171', color: '#fca5a5', padding: '15px', borderRadius: '10px', marginBottom: '20px', fontSize: '0.9rem' }}>
              {error}
            </div>
          )}

          {/* STEP 2: RESTAURANT PROFILE SURVEY */}
          {step === 2 && (
            <div>
              <div style={styles.sectionTitle}>
                <span>Step 2: Restaurant Survey Profile</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '-10px 0 25px 0' }}>Help us configure the layout defaults for your restaurant system.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>What type of food service operation is this?</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    {['QSR', 'Cafe', 'Fine Dining', 'Bar & Pub', 'Cloud Kitchen', 'Food Truck'].map(t => (
                      <div key={t} onClick={() => setSurvey({ ...survey, operation_type: t })} style={styles.optionCard(survey.operation_type === t)}>
                        <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>{t}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>What is your primary goal with our POS platform?</label>
                  <select style={styles.select} value={survey.goal} onChange={e => setSurvey({ ...survey, goal: e.target.value })}>
                    <option value="Speed up billing & checkout">Speed up billing & checkout</option>
                    <option value="Real-time inventory tracking">Real-time inventory tracking</option>
                    <option value="Multi-branch reporting & analytics">Multi-branch reporting & analytics</option>
                    <option value="Employee & shifts management">Employee & shifts management</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Daily Order Volume Estimate</label>
                  <select style={styles.select} value={survey.daily_orders} onChange={e => setSurvey({ ...survey, daily_orders: e.target.value })}>
                    <option value="Under 50">Under 50 orders</option>
                    <option value="50 - 200">50 - 200 orders</option>
                    <option value="200 - 500">200 - 500 orders</option>
                    <option value="500+">500+ orders</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>Do you have tables for dining in?</label>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    {['Yes', 'No'].map(o => (
                      <button key={o} onClick={() => setSurvey({ ...survey, dine_in: o })} style={{ ...styles.btnPrev, background: survey.dine_in === o ? '#6366f1' : '#334155', flex: 1 }}>
                        {o === 'Yes' ? 'Yes, table management is critical' : 'No, we are takeaway/delivery only'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={styles.footer}>
                <button onClick={() => navigate('/')} style={styles.btnPrev}>Cancel</button>
                <button onClick={handleNext} style={styles.btnNext}>Next Step: Setup Organizations →</button>
              </div>
            </div>
          )}

          {/* STEP 3: COMPANY SETUP */}
          {step === 3 && (
            <div>
              <div style={styles.sectionTitle}>
                <span>Step 3: Company Setup</span>
              </div>

              <div style={styles.grid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Organization Name *</label>
                  <input style={styles.input} type="text" value={tenant.tenant_name} onChange={e => setTenant({ ...tenant, tenant_name: e.target.value })} placeholder="Acme Food Group" />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Super Admin Email *</label>
                  <input style={styles.input} type="email" value={tenant.admin_email} onChange={e => setTenant({ ...tenant, admin_email: e.target.value })} placeholder="admin@restaurant.com" />
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Super Admin Password *</label>
                  <input style={styles.input} type="text" value={tenant.admin_password} onChange={e => setTenant({ ...tenant, admin_password: e.target.value })} placeholder="Password" />
                </div>
                <div style={{ ...styles.inputGroup, position: 'relative' }}>
                  <label style={styles.label}>Country</label>
                  <input
                    style={styles.input}
                    placeholder="Search or Select Country"
                    value={showCountryDropdown ? countrySearch : (tenant.country || '')}
                    onChange={e => {
                      setCountrySearch(e.target.value);
                      setShowCountryDropdown(true);
                    }}
                    onFocus={() => {
                      setCountrySearch('');
                      setShowCountryDropdown(true);
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowCountryDropdown(false), 200);
                    }}
                  />
                  {showCountryDropdown && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0,
                      maxHeight: '200px', overflowY: 'auto',
                      background: '#1e293b', border: '1px solid #475569',
                      borderRadius: '8px', zIndex: 10, marginTop: '4px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.5)'
                    }}>
                      {Object.keys(countryHierarchy)
                        .filter(c => c.toLowerCase().includes(countrySearch.toLowerCase()))
                        .map(c => (
                          <div
                            key={c}
                            style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #334155', color: '#f8fafc', fontSize: '0.95rem' }}
                            onMouseDown={() => {
                              setTenant({ ...tenant, country: c, state: '', city: '', address: '', phone: '' });
                              setCountrySearch('');
                              setShowCountryDropdown(false);
                            }}
                            onMouseEnter={e => e.target.style.background = '#334155'}
                            onMouseLeave={e => e.target.style.background = 'transparent'}
                          >
                            {c}
                          </div>
                        ))}
                      {Object.keys(countryHierarchy).filter(c => c.toLowerCase().includes(countrySearch.toLowerCase())).length === 0 && (
                        <div style={{ padding: '10px 15px', color: '#94a3b8', fontSize: '0.95rem' }}>No countries found</div>
                      )}
                    </div>
                  )}
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    {tenant.country && countryHierarchy[tenant.country]
                      ? countryHierarchy[tenant.country][0]
                      : 'State/Region'}
                  </label>
                  <input style={styles.input} type="text" value={tenant.state} onChange={e => setTenant({ ...tenant, state: e.target.value })} placeholder={tenant.country && countryHierarchy[tenant.country] ? `Enter ${countryHierarchy[tenant.country][0]}` : "Dubai / California"} />
                </div>
                {tenant.country && countryHierarchy[tenant.country] && countryHierarchy[tenant.country].length > 1 && (
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      {countryHierarchy[tenant.country][1]}
                    </label>
                    <input style={styles.input} type="text" value={tenant.city || ''} onChange={e => setTenant({ ...tenant, city: e.target.value })} placeholder={`Enter ${countryHierarchy[tenant.country][1]}`} />
                  </div>
                )}
                {tenant.country && countryHierarchy[tenant.country] && countryHierarchy[tenant.country].length > 2 && (
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>
                      {countryHierarchy[tenant.country][2]}
                    </label>
                    <input style={styles.input} type="text" value={tenant.address || ''} onChange={e => setTenant({ ...tenant, address: e.target.value })} placeholder={`Enter ${countryHierarchy[tenant.country][2]}`} />
                  </div>
                )}
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Phone Number</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', width: '70px', textAlign: 'center', flexShrink: 0, padding: '12px 0' }}>
                      {tenant.country && countryMeta[tenant.country] ? countryMeta[tenant.country].phone_code : '+--'}
                    </div>
                    <input
                      style={{ ...styles.input, flex: 1 }}
                      type="text"
                      value={tenant.phone}
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '');
                        setTenant({ ...tenant, phone: val });
                      }}
                      maxLength={tenant.country && countryMeta[tenant.country] ? countryMeta[tenant.country].phone_length : 15}
                      placeholder={tenant.country && countryMeta[tenant.country] ? `Enter ${countryMeta[tenant.country].phone_length} digit number` : "Select country"}
                    />
                  </div>
                </div>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>Currency Symbol</label>
                  <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {tenant.country && countryMeta[tenant.country] && countryMeta[tenant.country].currency ? (
                      <>
                        <CurrencySymbol currencyCode={countryMeta[tenant.country].currency} size={18} />
                        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{countryMeta[tenant.country].currency}</span>
                      </>
                    ) : 'Select country first'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '25px' }}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={hasMultipleCompanies} onChange={e => setHasMultipleCompanies(e.target.checked)} />
                  Do you have multiple companies?
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={hasBranches} onChange={e => setHasBranches(e.target.checked)} />
                  Do you have multiple branches?
                </label>
              </div>

              {/* DYNAMIC COMPANIES FIELDS */}
              {hasMultipleCompanies && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Secondary Companies Setup</h4>
                    <button onClick={addCompany} style={{ ...styles.btnPrev, background: '#10b981', color: '#ffffff', padding: '6px 12px', fontSize: '0.8rem' }}>+ Add Company</button>
                  </div>
                  {companies.map((c, index) => (
                    <div key={c.id} style={{ background: '#0f172a', padding: '20px', borderRadius: '12px', border: '1px solid #334155', marginBottom: '15px', position: 'relative' }}>
                      {companies.length > 1 && (
                        <button onClick={() => removeCompany(c.id)} style={{ position: 'absolute', top: '15px', right: '15px', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer' }}>Remove</button>
                      )}
                      <h5 style={{ margin: '0 0 15px 0' }}>Company #{index + 1}</h5>
                      <div style={styles.grid}>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Company Name *</label>
                          <input style={styles.input} type="text" value={c.company_name} onChange={e => updateCompany(c.id, 'company_name', e.target.value)} placeholder="Company Name" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Admin Email *</label>
                          <input style={styles.input} type="email" value={c.admin_email} onChange={e => updateCompany(c.id, 'admin_email', e.target.value)} placeholder="admin@comp.com" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Admin Password *</label>
                          <input style={styles.input} type="text" value={c.admin_password} onChange={e => updateCompany(c.id, 'admin_password', e.target.value)} placeholder="Password" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Country (Optional)</label>
                          <select style={styles.select} value={c.country || ''} onChange={e => updateCompany(c.id, 'country', e.target.value)}>
                            <option value="">-- Select Country --</option>
                            {Object.keys(countryHierarchy).map(cnt => <option key={cnt} value={cnt}>{cnt}</option>)}
                          </select>
                        </div>
                        {c.country && (
                          <>
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>{countryHierarchy[c.country] ? countryHierarchy[c.country][0] : 'State/Region'}</label>
                              <input style={styles.input} type="text" value={c.state || ''} onChange={e => updateCompany(c.id, 'state', e.target.value)} placeholder={countryHierarchy[c.country] ? `Enter ${countryHierarchy[c.country][0]}` : "State/Region"} />
                            </div>
                            {countryHierarchy[c.country] && countryHierarchy[c.country].length > 1 && (
                              <div style={styles.inputGroup}>
                                <label style={styles.label}>{countryHierarchy[c.country][1]}</label>
                                <input style={styles.input} type="text" value={c.city || ''} onChange={e => updateCompany(c.id, 'city', e.target.value)} placeholder={`Enter ${countryHierarchy[c.country][1]}`} />
                              </div>
                            )}
                            {countryHierarchy[c.country] && countryHierarchy[c.country].length > 2 && (
                              <div style={styles.inputGroup}>
                                <label style={styles.label}>{countryHierarchy[c.country][2]}</label>
                                <input style={styles.input} type="text" value={c.address || ''} onChange={e => updateCompany(c.id, 'address', e.target.value)} placeholder={`Enter ${countryHierarchy[c.country][2]}`} />
                              </div>
                            )}
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>Phone Number</label>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', width: '70px', textAlign: 'center', flexShrink: 0, padding: '12px 0' }}>
                                  {countryMeta[c.country] ? countryMeta[c.country].phone_code : '+--'}
                                </div>
                                <input style={{ ...styles.input, flex: 1 }} type="text" value={c.phone || ''} onChange={e => updateCompany(c.id, 'phone', e.target.value.replace(/\D/g, ''))} maxLength={countryMeta[c.country] ? countryMeta[c.country].phone_length : 15} placeholder={countryMeta[c.country] ? `Enter ${countryMeta[c.country].phone_length} digit number` : "Phone"} />
                              </div>
                            </div>
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>Currency Symbol</label>
                              <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {countryMeta[c.country] && countryMeta[c.country].currency ? (
                                  <>
                                    <CurrencySymbol currencyCode={countryMeta[c.country].currency} size={18} />
                                    <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{countryMeta[c.country].currency}</span>
                                  </>
                                ) : 'N/A'}
                              </div>
                            </div>
                          </>
                        )}

                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* DYNAMIC BRANCHES FIELDS */}
              {hasBranches && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h4 style={{ fontSize: '1.1rem', fontWeight: '600' }}>Branches Setup</h4>
                    <button onClick={addBranch} style={{ ...styles.btnPrev, background: '#10b981', color: '#ffffff', padding: '6px 12px', fontSize: '0.8rem' }}>+ Add Branch</button>
                  </div>
                  {branches.map((b, index) => (
                    <div key={b.id} style={{ background: '#0f172a', padding: '20px', borderRadius: '12px', border: '1px solid #334155', marginBottom: '15px', position: 'relative' }}>
                      {branches.length > 1 && (
                        <button onClick={() => removeBranch(b.id)} style={{ position: 'absolute', top: '15px', right: '15px', background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '4px 8px', fontSize: '0.75rem', cursor: 'pointer' }}>Remove</button>
                      )}
                      <h5 style={{ margin: '0 0 15px 0' }}>Branch #{index + 1}</h5>
                      <div style={styles.grid}>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Link to Company *</label>
                          <select
                            style={styles.select}
                            value={b.company_name}
                            onChange={e => updateBranch(b.id, 'company_name', e.target.value)}
                            required
                          >
                            <option value="">-- Select Company --</option>
                            {tenant.tenant_name && (
                              <option value={tenant.tenant_name}>{tenant.tenant_name} (Primary)</option>
                            )}
                            {companies.filter(c => c.company_name && c.company_name !== tenant.tenant_name).map(c => (
                              <option key={c.id} value={c.company_name}>{c.company_name}</option>
                            ))}
                          </select>
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Branch Name *</label>
                          <input style={styles.input} type="text" value={b.branch_name} onChange={e => updateBranch(b.id, 'branch_name', e.target.value)} placeholder="Main Branch / Downtown" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Admin Email *</label>
                          <input style={styles.input} type="email" value={b.admin_email} onChange={e => updateBranch(b.id, 'admin_email', e.target.value)} placeholder="branchadmin@comp.com" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Admin Password *</label>
                          <input style={styles.input} type="text" value={b.admin_password} onChange={e => updateBranch(b.id, 'admin_password', e.target.value)} placeholder="Password" />
                        </div>
                        <div style={styles.inputGroup}>
                          <label style={styles.label}>Country (Optional)</label>
                          <select style={styles.select} value={b.country || ''} onChange={e => updateBranch(b.id, 'country', e.target.value)}>
                            <option value="">-- Select Country --</option>
                            {Object.keys(countryHierarchy).map(cnt => <option key={cnt} value={cnt}>{cnt}</option>)}
                          </select>
                        </div>
                        {b.country && (
                          <>
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>{countryHierarchy[b.country] ? countryHierarchy[b.country][0] : 'State/Region'}</label>
                              <input style={styles.input} type="text" value={b.state || ''} onChange={e => updateBranch(b.id, 'state', e.target.value)} placeholder={countryHierarchy[b.country] ? `Enter ${countryHierarchy[b.country][0]}` : "State/Region"} />
                            </div>
                            {countryHierarchy[b.country] && countryHierarchy[b.country].length > 1 && (
                              <div style={styles.inputGroup}>
                                <label style={styles.label}>{countryHierarchy[b.country][1]}</label>
                                <input style={styles.input} type="text" value={b.city || ''} onChange={e => updateBranch(b.id, 'city', e.target.value)} placeholder={`Enter ${countryHierarchy[b.country][1]}`} />
                              </div>
                            )}
                            {countryHierarchy[b.country] && countryHierarchy[b.country].length > 2 && (
                              <div style={styles.inputGroup}>
                                <label style={styles.label}>{countryHierarchy[b.country][2]}</label>
                                <input style={styles.input} type="text" value={b.address || ''} onChange={e => updateBranch(b.id, 'address', e.target.value)} placeholder={`Enter ${countryHierarchy[b.country][2]}`} />
                              </div>
                            )}
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>Phone Number</label>
                              <div style={{ display: 'flex', gap: '10px' }}>
                                <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', width: '70px', textAlign: 'center', flexShrink: 0, padding: '12px 0' }}>
                                  {countryMeta[b.country] ? countryMeta[b.country].phone_code : '+--'}
                                </div>
                                <input style={{ ...styles.input, flex: 1 }} type="text" value={b.phone || ''} onChange={e => updateBranch(b.id, 'phone', e.target.value.replace(/\D/g, ''))} maxLength={countryMeta[b.country] ? countryMeta[b.country].phone_length : 15} placeholder={countryMeta[b.country] ? `Enter ${countryMeta[b.country].phone_length} digit number` : "Phone"} />
                              </div>
                            </div>
                            <div style={styles.inputGroup}>
                              <label style={styles.label}>Currency Symbol</label>
                              <div style={{ ...styles.input, backgroundColor: '#334155', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {countryMeta[b.country] && countryMeta[b.country].currency ? (
                                  <>
                                    <CurrencySymbol currencyCode={countryMeta[b.country].currency} size={18} />
                                    <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{countryMeta[b.country].currency}</span>
                                  </>
                                ) : 'N/A'}
                              </div>
                            </div>
                          </>
                        )}

                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.footer}>
                <button onClick={handlePrev} style={styles.btnPrev}>← Back</button>
                <button onClick={handleNext} disabled={!tenant.tenant_name} style={styles.btnNext}>Next Step: Plans & Modules →</button>
              </div>
            </div>
          )}

          {/* STEP 4: SUBSCRIPTION PLAN & MODULES */}
          {step === 4 && (
            <div>
              <div style={styles.sectionTitle}>
                <span>Step 4: Subscription Plan & POS Modules</span>
              </div>
              
              {/* BILLING CYCLE SELECTION */}
              {(() => {
                const selectedPlanName = tenant.subscription_plan || 'Basic';
                const selectedPlanObj = subscriptionPlansData[`${selectedPlanName} Plan`];
                const pricingTiers = selectedPlanObj?.pricingTiers || [];

                return null;
              })()}

              <div style={{ display: 'grid', gridTemplateColumns: showAddons ? '1fr 380px' : '1fr', gap: '30px', alignItems: 'start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <h3 style={{ margin: 0, color: '#f8fafc', fontSize: '1.2rem', fontWeight: '700' }}>Select Base Plan</h3>
                    <button 
                      onClick={(e) => { e.preventDefault(); setShowAddons(!showAddons); }}
                      style={{ background: showAddons ? '#334155' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', boxShadow: showAddons ? 'none' : '0 4px 10px rgba(59, 130, 246, 0.3)' }}
                    >
                      {showAddons ? 'Hide Add-ons' : 'Customize with Add-ons'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', alignItems: 'stretch' }}>
                    {Object.keys(subscriptionPlansData)
                      .filter(k => k !== 'MASTER_PALETTE' && k !== 'availableModules')
                      .sort((a, b) => {
                        const order = { 'Basic Plan': 1, 'Silver Plan': 2, 'Gold Plan': 3 };
                        const valA = order[a] || 99;
                        const valB = order[b] || 99;
                        return valA - valB;
                      })
                      .map(planName => {
                  const planObj = subscriptionPlansData[planName];
                  if (!planObj) return null;
                  
                  const p = {
                    name: planName.replace(' Plan', ''),
                    fullName: planName,
                    basePrice: 0
                  };
                  const planItems = planObj.items ? planObj.items : planObj;
                  const extraCost = planItems.reduce((sum, item) => sum + Number(item.price || 0), 0);
                  const baseMonthlyPrice = p.basePrice + extraCost;
                  const isPlanSelected = tenant.subscription_plan === p.name;
                  const pricingTiers = planObj?.pricingTiers || [];
                  
                  let finalPrice = baseMonthlyPrice;
                  let durationLabel = 'mo';
                  let activeTier = null;
                  
                  if (isPlanSelected && selectedBillingDuration > 1) {
                    activeTier = pricingTiers.find(t => Number(t.durationMonths) === selectedBillingDuration);
                    if (activeTier) {
                      finalPrice = Number(activeTier.discountAmount || 0);
                      durationLabel = activeTier.label;
                    } else {
                      finalPrice = baseMonthlyPrice * selectedBillingDuration;
                      durationLabel = `${selectedBillingDuration} mos`;
                    }
                  }

                  let dynamicDesc = '';
                  if (planItems.length > 0) {
                    const moduleNames = planItems.map(i => i.name).filter(Boolean);
                    if (moduleNames.length > 0) {
                      dynamicDesc = `Includes: ${moduleNames.slice(0, 4).join(', ')}${moduleNames.length > 4 ? ', and more.' : '.'}`;
                    } else {
                      dynamicDesc = 'Custom configured plan.';
                    }
                  } else {
                    dynamicDesc = 'No modules configured yet.';
                  }

                  return (
                    <div key={p.name} style={{
                      ...styles.planCard(isPlanSelected),
                      display: 'flex', flexDirection: 'column', position: 'relative',
                      boxShadow: isPlanSelected ? '0 10px 30px rgba(99, 102, 241, 0.2)' : 'none'
                    }}>
                      {/* Clickable Header Area to Select the Full Plan */}
                      <div
                        onClick={() => {
                          const newPlan = p.name;
                          if (tenant.subscription_plan !== newPlan) {
                            setTenant({ ...tenant, subscription_plan: newPlan });
                            setPurchasedAddons([]);
                            setSelectedBillingDuration(1); // Reset duration on plan change
                          }
                        }}
                        style={{ cursor: 'pointer', paddingBottom: '15px', borderBottom: '1px solid #334155', marginBottom: '15px' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ fontWeight: '800', fontSize: '1.2rem', color: isPlanSelected ? '#818cf8' : '#f8fafc' }}>{p.fullName}</span>
                          {isPlanSelected ? (
                            <span style={{ background: '#4f46e5', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold' }}>SELECTED</span>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #475569', padding: '2px 8px', borderRadius: '12px' }}>SELECT</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                          <div style={{ fontSize: '1.8rem', fontWeight: '900', color: isPlanSelected ? '#e0e7ff' : '#cbd5e1' }}>
                            ${finalPrice}
                            {activeTier && (
                              <span style={{ fontSize: '1.1rem', color: '#64748b', textDecoration: 'line-through', marginLeft: '8px', fontWeight: '500' }}>
                                ${baseMonthlyPrice.toFixed(2)}
                              </span>
                            )}
                            <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: '600', marginLeft: '5px' }}>
                              /{durationLabel}
                            </span>
                          </div>
                        </div>
                        {/* Billing Toggle: only on selected card, constrained inside card width */}
                        {isPlanSelected && pricingTiers.length > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px',
                            background: '#1e293b', padding: '6px 8px', borderRadius: '25px',
                            marginBottom: '15px', width: '100%', boxSizing: 'border-box',
                            border: '1px solid #334155', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)'
                          }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); setSelectedBillingDuration(1); }}
                              style={{
                                border: 'none', padding: '5px 12px', borderRadius: '20px',
                                fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', flex: '1 1 auto',
                                background: selectedBillingDuration === 1 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'transparent',
                                color: selectedBillingDuration === 1 ? '#fff' : '#94a3b8',
                                transition: 'all 0.3s',
                                boxShadow: selectedBillingDuration === 1 ? '0 4px 10px rgba(16, 185, 129, 0.3)' : 'none',
                                whiteSpace: 'nowrap'
                              }}
                            >Monthly</button>
                            {pricingTiers.map((tier, idx) => (
                              <button
                                key={idx}
                                onClick={(e) => { e.stopPropagation(); setSelectedBillingDuration(Number(tier.durationMonths)); }}
                                style={{
                                  border: 'none', padding: '5px 12px', borderRadius: '20px',
                                  fontSize: '0.78rem', fontWeight: '700', cursor: 'pointer', flex: '1 1 auto',
                                  background: selectedBillingDuration === Number(tier.durationMonths) ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'transparent',
                                  color: selectedBillingDuration === Number(tier.durationMonths) ? '#fff' : '#94a3b8',
                                  transition: 'all 0.3s',
                                  boxShadow: selectedBillingDuration === Number(tier.durationMonths) ? '0 4px 10px rgba(59, 130, 246, 0.3)' : 'none',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {tier.label}
                              </button>
                            ))}
                          </div>
                        )}
                        <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: '0', minHeight: '40px', lineHeight: '1.4' }}>{dynamicDesc}</p>
                      </div>

                      {/* Dynamic Scrollable Features List */}
                      {planItems.length > 0 && (
                        <div style={{
                          flex: 1, overflowY: 'auto',
                          display: 'flex', flexDirection: 'column', gap: '8px',
                          paddingRight: '5px'
                        }}>
                          {planItems.map((item, idx) => {
                            // Determine if owned via the currently selected base plan
                            const basePlanFullName = (tenant.subscription_plan || 'Basic') + ' Plan';
                            const bPlanObj = subscriptionPlansData[basePlanFullName];
                            const basePlanItems = bPlanObj && bPlanObj.items ? bPlanObj.items : bPlanObj || [];
                            const isModuleOwnedInBase = basePlanItems.some(bItem => bItem.name === item.name);

                            // Determine item state
                            const isModuleAddon = purchasedAddons.some(a => a.moduleId === item.id && !a.pageId);
                            let itemColor = '#94a3b8';
                            let itemIcon = null;
                            let itemStatus = 'unpurchased';

                            if (isPlanSelected || isModuleOwnedInBase) {
                              itemColor = '#10b981'; // Emerald (Included)
                              itemIcon = <CheckCircle size={14} style={{ color: '#10b981', flexShrink: 0 }} />;
                              itemStatus = 'included';
                            } else if (isModuleAddon) {
                              itemColor = '#60a5fa'; // Blue (Addon)
                              itemIcon = <CheckCircle size={14} style={{ color: '#60a5fa', flexShrink: 0 }} />;
                              itemStatus = 'addon';
                            } else {
                              itemColor = '#64748b'; // Darker text for unpurchased
                              itemIcon = <div style={{ border: '2px solid #334155', borderRadius: '50%', width: '14px', height: '14px', flexShrink: 0 }}></div>;
                            }

                            return (
                              <details key={idx} style={{ fontSize: '0.85rem', color: '#cbd5e1', background: itemStatus === 'unpurchased' ? 'rgba(0, 0, 0, 0.4)' : 'rgba(15, 23, 42, 0.4)', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${itemStatus === 'included' ? '#065f46' : itemStatus === 'addon' ? '#1e3a8a' : '#000000'}` }}>
                                <summary
                                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', outline: 'none', padding: '10px', listStyle: 'none', background: itemStatus === 'included' ? 'rgba(16, 185, 129, 0.1)' : itemStatus === 'addon' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(0,0,0,0.2)', transition: 'all 0.2s' }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    {itemIcon}
                                    <span style={{ fontWeight: itemStatus === 'unpurchased' ? '500' : '600', color: itemColor }}>{item.name}</span>
                                    {itemStatus === 'addon' && <span style={{ fontSize: '0.65rem', background: '#3b82f6', color: '#fff', padding: '2px 6px', borderRadius: '10px', marginLeft: 'auto', marginRight: '5px', fontWeight: 'bold' }}>ADD-ON</span>}
                                  </div>
                                </summary>
                                {item.pages && item.pages.length > 0 && (
                                  <div style={{ padding: '8px 10px 10px 30px', display: 'flex', flexDirection: 'column', gap: '6px', background: 'rgba(0,0,0,0.3)' }}>
                                    {item.pages.map(page => {
                                      const isPageOwnedInBase = basePlanItems.some(bItem => bItem.name === item.name && bItem.pages && bItem.pages.some(pg => pg.name === page.name));
                                      const isPageAddon = purchasedAddons.some(a => a.moduleId === item.id && a.pageId === page.id);
                                      let pageColor = '#64748b';
                                      let pageAction = null;

                                      if (isPlanSelected || isPageOwnedInBase) {
                                        pageColor = '#10b981';
                                        pageAction = <CheckCircle size={12} style={{ color: '#10b981' }} />;
                                      } else if (isModuleAddon || isPageAddon) {
                                        pageColor = '#60a5fa';
                                        pageAction = <span style={{ fontSize: '0.65rem', background: '#3b82f6', color: '#fff', padding: '1px 5px', borderRadius: '6px', fontWeight: 'bold' }}>ADD-ON</span>;
                                      } else {
                                        pageAction = null;
                                      }

                                      return (
                                        <div key={page.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: pageColor }}>
                                          <span style={{ fontSize: '0.8rem' }}>- {page.name}</span>
                                          {pageAction}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </details>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              </div>

              {/* AVAILABLE ADD-ONS SECTION */}
              {showAddons && (() => {
                const basePlanFullName = (tenant.subscription_plan || 'Basic') + ' Plan';
                const bPlanObj = subscriptionPlansData[basePlanFullName];
                const basePlanItems = bPlanObj && bPlanObj.items ? bPlanObj.items : (bPlanObj || []);

                // Use MASTER_MODULES_LIST to ensure all system modules are evaluated
                const allAvailableModules = [...MASTER_MODULES_LIST];

                // Filter out modules already completely in the base plan
                const unownedModules = allAvailableModules.map(mod => {
                  const baseMod = basePlanItems.find(bm => bm.name === mod.name);
                  
                  if (baseMod) {
                    // If base plan has it, check if any pages are missing
                    if (mod.pages && mod.pages.length > 0) {
                      const basePages = baseMod.pages || [];
                      const missingPages = mod.pages.filter(p => !basePages.some(bp => bp.name === p.name));
                      if (missingPages.length > 0) {
                        return { ...mod, pages: missingPages, isPartial: true };
                      }
                    }
                    return null; // Fully owned
                  }
                  return mod;
                }).filter(Boolean);

                if (unownedModules.length === 0) return null;

                return (
                  <div style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(71, 85, 105, 0.4)', borderRadius: '16px', padding: '25px', marginBottom: '30px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#f8fafc', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
                      <span style={{ color: '#10b981', fontSize: '1.4rem' }}>+</span> Available Add-ons
                    </h4>
                    <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: '20px' }}>Customize your experience by purchasing additional features separately.</p>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', maxHeight: '600px', overflowY: 'auto', paddingRight: '5px' }}>
                      {unownedModules.map(mod => {
                        const isModuleAddon = purchasedAddons.some(a => a.moduleId === mod.id && !a.pageId);
                        
                        return (
                          <div key={mod.id} style={{ background: 'rgba(15, 23, 42, 0.6)', border: `1px solid ${isModuleAddon ? '#3b82f6' : (mod.isPartial ? '#eab308' : '#334155')}`, borderRadius: '10px', padding: '15px', position: 'relative' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                              <span style={{ fontWeight: '600', color: '#f8fafc', fontSize: '0.95rem' }}>{mod.name}</span>
                              {isModuleAddon ? (
                                <span style={{ fontSize: '0.7rem', background: '#3b82f6', color: '#fff', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>ADDED</span>
                              ) : mod.isPartial ? (
                                <span style={{ fontSize: '0.7rem', background: '#eab308', color: '#000', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>PARTIAL</span>
                              ) : (
                                <button 
                                  onClick={(e) => { e.preventDefault(); openAddonModal(mod, null, 'Custom Addon'); }}
                                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 5px rgba(16, 185, 129, 0.3)' }}
                                  onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                  onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                                >+ ADD MODULE</button>
                              )}
                            </div>
                            {!mod.isPartial && <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '1rem', marginBottom: '10px' }}>${mod.price}/mo</div>}
                            
                            {mod.pages && mod.pages.length > 0 && (
                              <details open={mod.isPartial} style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                <summary style={{ cursor: 'pointer', outline: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 0' }}>
                                  <span>{mod.isPartial ? 'Missing Pages' : 'View Pages'}</span> <span style={{ fontSize: '10px' }}>▼</span>
                                </summary>
                                <div style={{ marginTop: '8px', paddingLeft: '5px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                  {mod.pages.map(page => {
                                    const isPageAddon = purchasedAddons.some(a => a.moduleId === mod.id && a.pageId === page.id);
                                    return (
                                      <div key={page.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ color: '#cbd5e1' }}>- {page.name}</span>
                                        {!isModuleAddon && (
                                          isPageAddon ? (
                                            <span style={{ fontSize: '0.65rem', background: '#3b82f6', color: '#fff', padding: '1px 5px', borderRadius: '6px', fontWeight: 'bold' }}>ADDED</span>
                                          ) : (
                                            <button
                                              onClick={(e) => { e.preventDefault(); openAddonModal(mod, page, 'Custom Addon'); }}
                                              style={{ background: '#334155', color: '#cbd5e1', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '0.65rem', cursor: 'pointer', transition: 'background 0.2s' }}
                                              onMouseOver={(e) => { e.target.style.background = '#475569'; e.target.style.color = '#fff'; }}
                                              onMouseOut={(e) => { e.target.style.background = '#334155'; e.target.style.color = '#cbd5e1'; }}
                                            >
                                              + ADD
                                            </button>
                                          )
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </details>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              </div>

              {/* ADD-ONS SUMMARY BOX */}
              {purchasedAddons.length > 0 && (
                <div style={{ background: 'rgba(30, 58, 138, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', borderRadius: '16px', padding: '25px', marginBottom: '30px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#93c5fd', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' }}>
                    <ShieldCheck size={22} /> Individually Purchased Add-ons
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                    {purchasedAddons.map((addon, idx) => (
                      <div key={idx} style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid #3b82f6', borderRadius: '10px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ padding: '15px', position: 'relative' }}>
                          <button onClick={() => removeAddon(idx)} style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(239, 68, 68, 0.1)', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '1rem', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', zIndex: 2 }} onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'} onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}>&times;</button>
                          <span style={{ display: 'block', fontSize: '0.7rem', color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', marginBottom: '4px' }}>{addon.sourcePlan}</span>
                          <span style={{ display: 'block', fontWeight: '600', color: '#f8fafc', fontSize: '0.95rem', paddingRight: '20px', lineHeight: '1.3' }}>{addon.pageName || addon.moduleName}</span>
                          <span style={{ display: 'block', color: '#10b981', fontWeight: 'bold', marginTop: '6px', fontSize: '1.1rem' }}>${addon.price}/mo</span>
                        </div>
                        {addon.pages && addon.pages.length > 0 && (
                          <details style={{ background: 'rgba(0,0,0,0.4)', borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
                            <summary style={{ fontSize: '0.75rem', color: '#93c5fd', padding: '8px 15px', cursor: 'pointer', outline: 'none', listStyle: 'none', display: 'flex', justifyContent: 'space-between' }}>
                              <span>Included Pages</span>
                              <span>▼</span>
                            </summary>
                            <div style={{ padding: '0 15px 10px 15px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {addon.pages.map((p, pIdx) => (
                                <div key={pIdx} style={{ fontSize: '0.7rem', color: '#cbd5e1', display: 'flex', justifyContent: 'space-between' }}>
                                  <span>- {p.name}</span>
                                  {Number(p.price || 0) > 0 && <span style={{ color: '#94a3b8' }}>+${p.price}/mo</span>}
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px dashed rgba(59, 130, 246, 0.4)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#94a3b8', fontSize: '1rem', fontWeight: '600' }}>Total Add-ons Cost:</span>
                    <span style={{ fontSize: '1.6rem', fontWeight: '900', color: '#60a5fa', textShadow: '0 2px 10px rgba(96, 165, 250, 0.3)' }}>${purchasedAddons.reduce((sum, a) => sum + Number(a.price || 0), 0) * selectedBillingDuration}/{selectedBillingDuration === 1 ? 'mo' : selectedBillingDuration + ' mos'}</span>
                  </div>
                </div>
              )}

              {/* MODAL POPUP */}
              {addonModal.isOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(2, 6, 23, 0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(12px)', padding: '20px' }}>
                  <div style={{
                    background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                    padding: '35px 30px',
                    borderRadius: '24px',
                    width: '100%',
                    maxWidth: '420px',
                    border: '1px solid #334155',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(99, 102, 241, 0.1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Top Glow Accent */}
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)' }}></div>

                    {/* Header with Icon */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))', border: '1px solid rgba(139, 92, 246, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ fontSize: '24px' }}>✨</span>
                      </div>
                      <div>
                        <h3 style={{ margin: '0 0 5px 0', color: '#f8fafc', fontSize: '1.4rem', fontWeight: '800', letterSpacing: '-0.5px' }}>Unlock Add-on</h3>
                        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Enhance your current subscription</p>
                      </div>
                    </div>

                    <div style={{ background: 'rgba(15, 23, 42, 0.6)', borderRadius: '16px', padding: '20px', marginBottom: '25px', border: '1px solid #1e293b' }}>
                      <p style={{ color: '#94a3b8', fontSize: '0.95rem', margin: '0 0 15px 0', lineHeight: '1.6' }}>
                        You are about to add <strong style={{ color: '#f8fafc', borderBottom: '1px solid #4f46e5' }}>{addonModal.page ? addonModal.page.name : addonModal.module.name}</strong> from the <span style={{ color: '#818cf8', fontWeight: '600' }}>{addonModal.planName}</span> to your workspace.
                      </p>

                      {addonModal.missingDependencies && addonModal.missingDependencies.length > 0 && (
                        <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '12px 15px', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.2)', marginBottom: '15px' }}>
                          <p style={{ margin: '0 0 8px 0', color: '#10b981', fontSize: '0.8rem', fontWeight: '700', textTransform: 'uppercase' }}>Required Dependencies:</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {addonModal.missingDependencies.map((dep, idx) => (
                              <details key={idx} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '6px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                                <summary style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#a7f3d0', fontSize: '0.85rem', padding: '8px', listStyle: 'none', outline: 'none' }}>
                                  <input
                                    type="checkbox"
                                    checked={dep.checked}
                                    onChange={(e) => {
                                      const updated = [...addonModal.missingDependencies];
                                      updated[idx].checked = e.target.checked;
                                      setAddonModal({ ...addonModal, missingDependencies: updated });
                                    }}
                                    style={{
                                      accentColor: '#10b981',
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer',
                                      marginRight: '5px'
                                    }}
                                  />
                                  <span style={{ flex: 1 }}>{dep.name} (+${dep.price}/mo)</span>
                                  {dep.pages && dep.pages.length > 0 && <span style={{ fontSize: '10px', color: '#10b981' }}>▼</span>}
                                </summary>
                                {dep.pages && dep.pages.length > 0 && (
                                  <div style={{ padding: '5px 10px 10px 35px', display: 'flex', flexDirection: 'column', gap: '4px', background: 'rgba(0,0,0,0.3)' }}>
                                    {dep.pages.map((p, pIdx) => (
                                      <div key={pIdx} style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'flex', justifyContent: 'space-between' }}>
                                        <span>- {p.name}</span>
                                        {Number(p.price || 0) > 0 && <span>+${p.price}/mo</span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </details>
                            ))}
                          </div>
                        </div>
                      )}

                      <div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)', padding: '20px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(16, 185, 129, 0.2)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: '700', color: '#10b981', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Monthly Cost</span>
                          <span style={{ fontSize: '2.2rem', fontWeight: '900', color: '#f8fafc', lineHeight: '1' }}>
                            ${(() => {
                              let total = addonModal.page ? Number(addonModal.page.price || 0) : Number(addonModal.module.price || 0);
                              if (addonModal.missingDependencies) {
                                addonModal.missingDependencies.forEach(dep => {
                                  if (dep.checked) total += Number(dep.price || 0);
                                });
                              }
                              return total;
                            })()}
                            <span style={{ fontSize: '1.1rem', color: '#64748b', fontWeight: '600' }}>/mo</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '12px' }}>
                      <button onClick={() => setAddonModal({ isOpen: false, module: null, page: null, planName: '' })} style={{ flex: 1, padding: '16px', background: 'transparent', color: '#94a3b8', border: '1px solid #334155', borderRadius: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', fontSize: '1rem' }} onMouseOver={e => { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.color = '#f8fafc'; }} onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}>Cancel</button>
                      <button onClick={handlePurchaseAddon} style={{ flex: 1, padding: '16px', background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)', color: '#ffffff', border: 'none', borderRadius: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 8px 20px -4px rgba(79, 70, 229, 0.5), inset 0 1px 1px rgba(255,255,255,0.2)', transition: 'all 0.2s', fontSize: '1rem', position: 'relative', overflow: 'hidden' }} onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}>
                        Confirm Purchase
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.footer}>
                {searchParams.get('step') === '4' ? (
                  <button onClick={handleSubmit} disabled={loading} style={{ ...styles.btnNext, width: '100%' }}>
                    {loading ? 'Upgrading Plan...' : 'Upgrade / Save Plan ✨'}
                  </button>
                ) : (
                  <>
                    <button onClick={handlePrev} style={styles.btnPrev}>← Back</button>
                    <button onClick={handleNext} style={styles.btnNext}>Next Step: Compliance Uploads →</button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP 5: COMPLIANCE DOCUMENTS */}
          {step === 5 && (
            <div>
              <div style={styles.sectionTitle}>
                <span>Step 5: Compliance Documents Registration</span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', margin: '-10px 0 25px 0' }}>Attach relevant documents for validation (GST, FSSAI, Accreditations).</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {complianceDocs.map((doc, idx) => (
                  <div key={doc.doc_type} style={{ background: '#0f172a', padding: '20px', borderRadius: '12px', border: '1px solid #334155' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                      <FileText size={18} style={{ color: '#6366f1' }} />
                      <span style={{ fontWeight: '700' }}>{doc.doc_type}</span>
                    </div>

                    <div style={styles.grid}>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          {doc.doc_type === 'Trade License' ? 'Trade Name' :
                           doc.doc_type === 'Accreditation Certificate' ? 'Accreditation Name' : 'Trade Name / Business Name'}
                        </label>
                        <input style={styles.input} type="text" value={doc.trade_name} onChange={e => updateComplianceField(idx, 'trade_name', e.target.value)} placeholder="Name" />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          {doc.doc_type === 'Trade License' ? 'Trade License Number' :
                           doc.doc_type === 'GST / VAT Certificate' ? 'GST / VAT Registration Number' :
                           doc.doc_type === 'FSSAI License' ? 'FSSAI License Number' : 'Accreditation / License Number'}
                        </label>
                        <input style={styles.input} type="text" value={doc.reg_number} onChange={e => updateComplianceField(idx, 'reg_number', e.target.value)} placeholder="Number" />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          {doc.doc_type === 'GST / VAT Certificate' ? 'Expiry Date (if applicable)' : 'Expiry Date'}
                        </label>
                        <input style={styles.input} type="date" value={doc.expiry_date} onChange={e => updateComplianceField(idx, 'expiry_date', e.target.value)} />
                      </div>
                      <div style={styles.inputGroup}>
                        <label style={styles.label}>
                          Upload {doc.doc_type.replace(' Certificate', '').replace(' License', '')} (PDF / Image)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#1e293b', border: '1px dashed #475569', borderRadius: '10px', padding: '12px', cursor: 'pointer', justifyContent: 'center' }}>
                          <Upload size={16} style={{ color: '#94a3b8' }} />
                          <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                            {doc.file ? doc.file.name : doc.file_name ? `Uploaded: ${doc.file_name}` : 'Choose File'}
                          </span>
                          <input type="file" onChange={e => handleFileChange(idx, e.target.files[0])} style={{ display: 'none' }} accept=".pdf,.png,.jpg,.jpeg" />
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.footer}>
                <button onClick={handlePrev} style={styles.btnPrev}>← Back</button>
                <button onClick={handleSubmit} disabled={loading} style={{ ...styles.btnNext, background: '#10b981', boxShadow: '0 4px 14px rgba(16, 185, 129, 0.4)' }}>
                  {loading ? 'Finalizing Setup...' : 'Complete SaaS Onboarding ✨'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardOrganization;
